/* =====================================================================
   Hubes Hub Custom Theme v1 — JS bundle
   Phase 1 only: announcement bar dismiss, mobile nav drawer.
   Cart drawer, predictive search, sticky vehicle banner ship in later
   phases. Keep this file small.

   Build sentinel: 2026-05-03-h3-prepop (forces content-hash refresh on
   Shopify CDN; H3 prePopulateFromStoredVehicle deployment from commit
   e703cec was not propagating despite "Theme upload complete"; bumping
   this comment to force content change → new asset_url ?v= cache-buster
   → fresh edge serve).
   ===================================================================== */

(function () {
  'use strict';

  // HHymm.configure at IIFE bootstrap (Pattern 4 race correction 2026-05-03).
  // Runs synchronously when hubeshub.js executes during defer phase: ymm.js
  // already defined window.HHymm (defer execution order); ready()-wrapped
  // init functions have not run yet (they're at the bottom of this IIFE).
  // Source URL passed via window-namespaced global set by inline <head>
  // script in layout/theme.liquid (Liquid asset_url interpolation lands in
  // the global before any defer execution).
  //
  // Defensive try/catch fall-through per Pattern 3: privacy modes, asset-
  // resolution edge cases, theme-editor transient states can throw. Existing
  // lazy-populate-on-focus path remains as the silent fallback if configure
  // fails or source is missing.
  if (window.HHymm && typeof window.HHymm.configure === 'function' && window.__hhYmmSource) {
    try { window.HHymm.configure({ source: window.__hhYmmSource }); } catch (e) { /* fall-through */ }
  }


  // Module-level reference to cart drawer's open/close API. Assigned by
  // initCartDrawer (3.5-1) at end of its setup; consumed by initAjaxAddToCart
  // (3.5-2) and future cart mutation handlers (3.5-3) to trigger drawer open
  // without relying on synthetic trigger.click() side effects.
  var cartDrawer = null;
  var cartVehicleModal = null; // { open, close } — set by initCartVehicleBar

  // ----- GA4 dataLayer helpers (3.5-5) -----
  // Per-pageview view_cart fire gate (Decision 1-A: single-fire per pageview).
  // Reset on bfcache restore via pageshow listener at bottom of IIFE.
  var viewCartFiredThisPageview = false;

  // item_brand sources from server-authoritative vendor (response for add_to_cart;
  // data-item-brand for DOM-read events). Different from PDP eyebrow which uses
  // custom.brand metafield with vendor fallback. Intentional: analytics is
  // response/server-driven; PDP UI is metafield-driven (richer brand display).
  // If vendor and custom.brand differ for some products, GA4 events show vendor
  // while PDP shows custom.brand. Don't sync them by adding metafield fetch to
  // events — different concerns.
  function buildCartItemFromLine (lineEl) {
    if (!lineEl) return null;
    var titleEl = lineEl.querySelector('.hh-cart-line__title');
    var qtyInput = lineEl.querySelector('[data-cart-qty-input]');
    return {
      item_id: lineEl.dataset.itemId || lineEl.dataset.lineKey || '',
      item_name: titleEl ? titleEl.textContent.trim() : '',
      item_brand: lineEl.dataset.itemBrand || '',
      price: parseFloat(lineEl.dataset.itemPrice) || 0,
      quantity: qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1
    };
  }

  // Build full-cart items[] payload for view_cart + begin_checkout (Decision 7-A).
  // DOM-read from drawer or main-cart section (Decision 9-A); both render the
  // same cart-drawer-line-item snippet so item DOM shape is identical.
  function buildFullCartItems (rootEl) {
    if (!rootEl) return [];
    var lines = rootEl.querySelectorAll('.hh-cart-line');
    var items = [];
    Array.prototype.forEach.call(lines, function (line) {
      var item = buildCartItemFromLine(line);
      if (item) items.push(item);
    });
    return items;
  }

  // Read cart total from drawer or page subtotal element (Shopify-formatted "$X.XX").
  function readCartTotalFromDom (rootEl) {
    if (!rootEl) return 0;
    var subtotalEl = rootEl.querySelector('[data-cart-subtotal]');
    if (!subtotalEl) return 0;
    var text = subtotalEl.textContent.replace(/[^0-9.]/g, '');
    return parseFloat(text) || 0;
  }

  // dataLayer push helper — defensive init (per CLAUDE.md GA4 pattern;
  // analytics.liquid initializes dataLayer in head before this runs, but
  // belt-and-suspenders against future analytics-load reorder).
  //
  // ── WHY THIS DUAL-EMITS (fixed 2026-08-24) ──────────────────────────
  // `view_cart` fired TWICE in 28 days against 57 add_to_cart. It was never
  // a wiring gap in the theme — this function was called correctly on both
  // the drawer-open and /cart paths. The events simply never reached GA4.
  //
  // Measured on the live storefront by sniffing GA4 /collect beacons while
  // walking a real PDP -> add-to-cart -> drawer-open path (positive control:
  // the sniffer had to see `view_item` first, or it was declared blind):
  //
  //   dataLayer shape pushed        reached GA4?
  //   ----------------------------  ------------
  //   add_to_cart  {event,ecommerce}   \_ yes, via the SECOND (gtag) push
  //   add_to_cart  gtag('event',...)   /  emitted by Shopify's own pixel
  //   view_cart    {event,ecommerce}      NO  <- only shape, never arrives
  //
  // So the GTM Custom Event shape is not converted to a GA4 event by
  // container GTM-5QBZJ7RR, while the gtag('event', ...) shape demonstrably
  // is — `blog_cta_click` reaches GA4 through exactly that call and nothing
  // else. The healthy-looking counts (view_item, add_to_cart, purchase,
  // begin_checkout) are Shopify's own pixel, NOT this function.
  //
  // The fix could live in the GTM console instead, by adding a Custom Event
  // trigger. It is done here on purpose: a theme change is version-controlled,
  // reviewable and testable, whereas the container is a surface this leg is
  // not authorized to change and which no test can see.
  //
  // ── AND WHY IT IS AN ALLOWLIST, NOT A BLANKET DUAL-EMIT ─────────────
  // Emitting every event through gtag would DOUBLE-COUNT the ones Shopify's
  // own pixel already delivers — which includes `add_to_cart`, the exact
  // metric the sold-out work is judged on. Inflating it here would fake our
  // own success. Measured GA4 counts over the same 28d window say which are
  // already covered:
  //
  //   covered by Shopify's pixel  -> add_to_cart 57 · view_item_list 775 ·
  //                                  begin_checkout 39   (do NOT re-emit)
  //   not covered, effectively 0  -> view_cart 2 · remove_from_cart 1
  //
  // So the allowlist below carries exactly the two that never arrive. It
  // FAILS CLOSED: a newly-added event emits on the GTM path only until
  // someone measures it and adds it here deliberately. The GTM push is kept
  // for every event so any existing container trigger keeps working, and
  // because `consent_state` proves non-Google tags consume that shape.
  //
  // EXPIRY CONDITION: if a GTM Custom Event trigger is ever added for
  // view_cart / remove_from_cart, this allowlist starts double-counting them
  // and must be emptied in the same change.
  var GTAG_FALLBACK_EVENTS = ['view_cart', 'remove_from_cart'];

  function fireDataLayerEvent (eventName, ecommerce) {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ecommerce: ecommerce
    });
    // The path with production evidence — allowlisted only. See above.
    if (GTAG_FALLBACK_EVENTS.indexOf(eventName) !== -1 && typeof window.gtag === 'function') {
      window.gtag('event', eventName, ecommerce || {});
    }
  }

  // Fire a non-ecommerce GA4 event (recovery-path interactions). Uses the
  // gtag shape only — these have no `ecommerce` payload and no GTM trigger,
  // and `blog_cta_click` is the in-production proof that this reaches GA4.
  function fireGaEvent (eventName, params) {
    if (typeof window.gtag !== 'function') return;
    window.gtag('event', eventName, params || {});
  }

  // ---------------------------------------------------------------
  // Announcement bar — dismiss with localStorage persistence
  // ---------------------------------------------------------------
  function initAnnouncement() {
    var bar = document.querySelector('[data-hh-announce]');
    if (!bar) return;

    var key = bar.dataset.dismissKey || 'hh-announce-dismissed';
    var dismissed = false;
    try { dismissed = localStorage.getItem(key) === '1'; } catch (e) {}

    if (dismissed) {
      bar.hidden = true;
      return;
    }

    var closeBtn = bar.querySelector('[data-hh-announce-close]');
    if (!closeBtn) return;

    closeBtn.addEventListener('click', function () {
      bar.hidden = true;
      try { localStorage.setItem(key, '1'); } catch (e) {}
    });
  }

  // ---------------------------------------------------------------
  // Mobile nav drawer
  // ---------------------------------------------------------------
  function initMobileNav() {
    var drawer = document.querySelector('[data-hh-mobile-nav]');
    var openBtn = document.querySelector('[data-hh-mobile-nav-open]');
    var closeBtn = drawer && drawer.querySelector('[data-hh-mobile-nav-close]');
    if (!drawer || !openBtn || !closeBtn) return;

    function open() {
      drawer.dataset.open = 'true';
      // inert handles aria-hidden + removes focusables from tab order natively;
      // toggling inert via .inert property avoids the axe aria-hidden-focus
      // violation that came from setting aria-hidden on a drawer that contained
      // focusable descendants when closed.
      drawer.inert = false;
      document.body.style.overflow = 'hidden';
      // Focus the close button so screen readers land somewhere sensible
      window.requestAnimationFrame(function () { closeBtn.focus(); });
    }

    function close() {
      drawer.dataset.open = 'false';
      drawer.inert = true;
      document.body.style.overflow = '';
      openBtn.focus();
    }

    openBtn.addEventListener('click', open);
    closeBtn.addEventListener('click', close);

    // Click on backdrop (drawer itself, but not the panel) closes
    drawer.addEventListener('click', function (e) {
      if (e.target === drawer) close();
    });

    // Escape key closes
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.dataset.open === 'true') close();
    });
  }

  // ---------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------
  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  // ---------------------------------------------------------------
  // ---------------------------------------------------------------
  // initPdpGallery (Phase 2 main-image arrow nav, Jodi UAT 2026-05-10)
  //
  // Wires interactivity on top of the CSS-only radio+label gallery in
  // snippets/pdp-gallery.liquid. The CSS handles main-image fade
  // transitions + active-thumb visual indicator via radio :checked
  // sibling selectors. JS adds:
  //   - Click handlers on prev/next arrow buttons (overlay on main image)
  //   - Keyboard arrow nav when gallery has focus (Left/Right)
  //   - Touch swipe (basic threshold; horizontal-axis-only)
  //   - Wrap-around at gallery boundaries (last+next → first; first+prev → last)
  //   - Count display sync ("3 / 14") on selection change
  //
  // Mechanism: navigate(dir) flips the .checked state on radio inputs
  // (which fires the CSS sibling-selector chain that updates main image
  // + thumb border). Native change event dispatched for any listeners.
  // ---------------------------------------------------------------
  function initPdpGallery () {
    var galleries = document.querySelectorAll('.hh-pdp-gallery');
    galleries.forEach(function (g) {
      var inputs = g.querySelectorAll('.hh-pdp-gallery__thumb-input');
      if (inputs.length <= 1) return;
      var prevBtn = g.querySelector('[data-pdp-gallery-prev]');
      var nextBtn = g.querySelector('[data-pdp-gallery-next]');
      var countEl = g.querySelector('[data-pdp-gallery-count]');

      function findCheckedIdx () {
        for (var i = 0; i < inputs.length; i++) if (inputs[i].checked) return i;
        return 0;
      }

      function navigate (dir) {
        var idx = findCheckedIdx();
        var target = (idx + dir + inputs.length) % inputs.length;  // wrap-around
        inputs[target].checked = true;
        // Dispatch change for any listeners (count sync, analytics, etc.)
        inputs[target].dispatchEvent(new Event('change', { bubbles: true }));
      }

      function updateCount () {
        if (!countEl) return;
        countEl.textContent = (findCheckedIdx() + 1) + ' / ' + inputs.length;
      }

      // Click handlers — prev/next arrows
      if (prevBtn) prevBtn.addEventListener('click', function () { navigate(-1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { navigate(+1); });

      // Keyboard nav — Left/Right arrow keys when gallery container has focus
      g.addEventListener('keydown', function (e) {
        // Skip native radio focus traversal — radios handle Up/Down within
        // group; we only intercept Left/Right when target isn't an input.
        if (e.target.matches('input[type="radio"]')) return;
        if (e.key === 'ArrowLeft') { e.preventDefault(); navigate(-1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); navigate(+1); }
      });

      // Touch swipe — bounded scope (50px threshold, horizontal axis only)
      var touchStartX = 0;
      var touchStartY = 0;
      var main = g.querySelector('.hh-pdp-gallery__main');
      if (main) {
        main.addEventListener('touchstart', function (e) {
          touchStartX = e.touches[0].clientX;
          touchStartY = e.touches[0].clientY;
        }, { passive: true });
        main.addEventListener('touchend', function (e) {
          var dx = e.changedTouches[0].clientX - touchStartX;
          var dy = e.changedTouches[0].clientY - touchStartY;
          // Require horizontal swipe (|dx| > |dy|) above threshold
          if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
            navigate(dx < 0 ? +1 : -1);
          }
        }, { passive: true });
      }

      // Count display sync — listen on each input for change events
      for (var i = 0; i < inputs.length; i++) {
        inputs[i].addEventListener('change', updateCount);
      }
      updateCount();  // initial state
    });
  }

  // ---------------------------------------------------------------
  // Click-to-copy (OEM PN, spec rows, alt part numbers)
  // Buttons use data-spec-copy. Source text comes from a sibling
  // [data-copy-text] attribute — the canonical value, preserved even
  // if the visible <code> wraps or truncates.
  // ---------------------------------------------------------------
  function initSpecCopy() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-spec-copy]');
      if (!btn) return;
      // The PartNumber component (snippets/part-number.liquid) owns both the value and the button.
      var container = btn.closest('.hh-pn');
      var src = container && container.querySelector('[data-copy-text]');
      var text = src ? src.getAttribute('data-copy-text') : '';
      if (!text || !navigator.clipboard) return;
      navigator.clipboard.writeText(text).then(function () {
        btn.dataset.copied = 'true';
        setTimeout(function () { btn.dataset.copied = 'false'; }, 1500);
      });
    });
  }

  // ---------------------------------------------------------------
  // PDP quantity +/− buttons
  // Reads min/max from the input. Step is always 1.
  // ---------------------------------------------------------------
  function initQty() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-qty-decrement], [data-qty-increment]');
      if (!btn) return;
      var qty = btn.closest('[data-qty]');
      var input = qty && qty.querySelector('[data-qty-input]');
      if (!input) return;
      var current = parseInt(input.value, 10);
      if (!Number.isFinite(current)) current = 1;
      var min = parseInt(input.min, 10);
      if (!Number.isFinite(min)) min = 1;
      var max = parseInt(input.max, 10);
      if (!Number.isFinite(max)) max = Infinity;
      var next = btn.matches('[data-qty-decrement]') ? Math.max(min, current - 1) : Math.min(max, current + 1);
      if (next !== current) {
        input.value = next;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  // ---------------------------------------------------------------
  // PDP variant pills
  // - Click updates aria-pressed on the option group's pills
  // - Resolves the variant matching the full option combination
  // - Updates the form's hidden id input + URL ?variant= param
  // - Dispatches CustomEvent('hh:variant-change', { detail: { variant } })
  //
  // Downstream listeners (price/stock/image swap, Stoq refresh) hook the
  // event without coupling to pill click logic. Variant data embedded
  // inline by pdp-info.liquid as <script data-product-variants>.
  // ---------------------------------------------------------------
  function initVariants() {
    document.addEventListener('click', function (e) {
      var pill = e.target.closest('.hh-pdp__variant-pill');
      if (!pill) return;
      if (pill.getAttribute('aria-disabled') === 'true') return;

      var pillsGroup = pill.closest('.hh-pdp__variant-pills');
      var form = pill.closest('form');
      if (!pillsGroup || !form) return;

      // Toggle aria-pressed within this option group
      Array.from(pillsGroup.querySelectorAll('.hh-pdp__variant-pill')).forEach(function (p) {
        p.setAttribute('aria-pressed', 'false');
        p.setAttribute('aria-checked', 'false');
      });
      pill.setAttribute('aria-pressed', 'true');
      pill.setAttribute('aria-checked', 'true');

      // Resolve current selection across ALL option groups in the form
      var optionGroups = Array.from(form.querySelectorAll('.hh-pdp__variants'));
      var selected = optionGroups.map(function (group) {
        var active = group.querySelector('[aria-pressed="true"]');
        return active ? active.getAttribute('data-option-value') : null;
      });

      // Look up matching variant from embedded JSON
      var dataEl = form.querySelector('[data-product-variants]') || document.querySelector('[data-product-variants]');
      if (!dataEl) return;
      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (err) { return; }
      var match = variants.find(function (v) {
        return selected.every(function (val, idx) {
          return val === null || (v.options && v.options[idx] === val);
        });
      });
      if (!match) return;

      // Update form id input
      var idInput = form.querySelector('[data-variant-id-input]');
      if (idInput) idInput.value = match.id;

      // Update URL ?variant= without reload
      try {
        var url = new URL(window.location.href);
        url.searchParams.set('variant', match.id);
        window.history.replaceState({}, '', url);
      } catch (err) { /* ignore */ }

      // Notify downstream listeners
      document.dispatchEvent(new CustomEvent('hh:variant-change', { detail: { variant: match } }));
    });
  }

  // ---------------------------------------------------------------
  // PDP fitment status — client upgrade
  //
  // Server renders the no-vehicle state. After page load (and on every
  // hh:vehicle-changed event from the vehicle-banner — Phase 2c), this
  // upgrades to fits / no-fit using HHymm.matches against the inlined
  // product fitment data.
  //
  // Server states the snippet ships with:
  //   - universal:    "Fits all vehicles" (universal_fitment="Yes" — overrides everything)
  //   - no-vehicle:   "Pick your vehicle to check fit" (default)
  // Client states this upgrades to:
  //   - fits:         "Fits your YYYY MAKE MODEL"
  //   - no-fit:       "Doesn't fit your YYYY MAKE MODEL — see the fitment table for compatible vehicles"
  // (verify-trim — Phase 2c+ refinement, when ambiguous trim signals exist)
  //
  // Definitive language by default; "May" reserved for genuine ambiguity.
  // ---------------------------------------------------------------
  var ICON_CHECK = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><polyline points="20 6 9 17 4 12"/></svg>';
  var ICON_ALERT = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>';
  var ICON_INFO = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>';
  var ICON_X = '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function vehicleLabel(v) {
    if (!v) return '';
    return [v.year, v.make, v.model].filter(Boolean).join(' ');
  }

  // Verdict wording comes from HHymm.verdictCopy (ymm.js) — ONE source for the
  // PDP buy box and the cart line (GH #3433). Escapes the headline and links the
  // copy's `link` substring when it carries one (the no-data state's "message Hubes").
  function verdictCopyHtml(copy) {
    if (!copy) return '';
    var html = escapeHtml(copy.headline || '');
    if (copy.link && copy.link.text) {
      var t = escapeHtml(copy.link.text);
      var i = html.indexOf(t);
      if (i !== -1) {
        html = html.slice(0, i) + '<a href="' + escapeHtml(copy.link.href || '#') + '">' + t + '</a>' + html.slice(i + t.length);
      }
    }
    return html;
  }

  function copyFor(verdict) {
    return (window.HHymm && typeof window.HHymm.verdictCopy === 'function') ? window.HHymm.verdictCopy(verdict) : null;
  }

  // Buyer-facing label for an exclusion key list, e.g. ['hybrid'] → "Hybrid".
  function exclusionLabel(keys) {
    return (keys || []).map(function (k) {
      k = String(k || '');
      return k.charAt(0).toUpperCase() + k.slice(1);
    }).join(' / ');
  }

  // ❌ recovery link → the curated browse target filtered for the buyer's
  // vehicle, using the SAME flat-facet URL contract the collection vehicle
  // filter writes (custom.fitment_years_text / _makes / _models).
  function buildRecoveryUrl(base, vehicle) {
    try {
      var url = new URL(base, window.location.origin);
      if (vehicle.year)  url.searchParams.set('filter.p.m.custom.fitment_years_text', String(vehicle.year));
      if (vehicle.make)  url.searchParams.set('filter.p.m.custom.fitment_makes', String(vehicle.make));
      if (vehicle.model) {
        url.searchParams.delete('filter.p.m.custom.fitment_models');
        modelFilterValues(vehicle.make, vehicle.model).forEach(function (m) {
          url.searchParams.append('filter.p.m.custom.fitment_models', m);
        });
      }
      return url.pathname + url.search;
    } catch (e) {
      return base;
    }
  }

  // ---------------------------------------------------------------
  // B4 no-vehicle in-buy-box picker — LOCAL Y/M/M off the ymm oracle.
  //
  // Renders in the no-vehicle state; on a complete selection funnels through
  // HHymm.setStoredVehicle (fires hh:vehicle-changed → updateFitmentStatus
  // re-renders the verdict in place). LOCAL-only: never calls the fitment API.
  // ---------------------------------------------------------------

  // Rebuild the picker markup when we re-enter no-vehicle from another state
  // (server renders the same structure on first paint). Mirrors the Liquid in
  // snippets/pdp-fitment-status.liquid — keep the two in sync.
  function buildNoVehiclePickerHtml(statusEl) {
    var base = escapeHtml((statusEl && statusEl.dataset.requestBase) || '/pages/request-a-part?source=ymm-not-listed');
    return '' +
      '<span class="hh-pdp__fitment-prompt" data-fitment-fallback hidden>Will this fit your vehicle? See the fitment table below to check.</span>' +
      '<div class="hh-pdp__ymm" data-fitment-picker>' +
        '<span class="hh-pdp__ymm-label" id="hh-pdp-ymm-label">Check if it fits your vehicle</span>' +
        '<div class="hh-pdp__ymm-selects" role="group" aria-labelledby="hh-pdp-ymm-label">' +
          '<select class="hh-pdp__ymm-select" data-ymm-year aria-label="Year"><option value="">Year</option></select>' +
          '<select class="hh-pdp__ymm-select" data-ymm-make aria-label="Make" disabled><option value="">Make</option></select>' +
          '<select class="hh-pdp__ymm-select" data-ymm-model aria-label="Model" disabled><option value="">Model</option></select>' +
        '</div>' +
        '<a class="hh-pdp__ymm-help" data-ymm-help href="' + base + '">Vehicle not listed? We may still be able to help &rarr;</a>' +
      '</div>';
  }

  // Keep the "not listed?" escape's href carrying whatever Y/M/M the buyer has
  // partially selected — the vehicle the oracle lacks + live demand, in one
  // signal (source=ymm-not-listed; feeds the ORACLE_SUPPLEMENT worklist).
  function updateYmmHelpHref(statusEl, picker) {
    var help = picker.querySelector('[data-ymm-help]');
    if (!help) return;
    var base = (statusEl && statusEl.dataset.requestBase) || '/pages/request-a-part?source=ymm-not-listed';
    var y = picker.querySelector('[data-ymm-year]');
    var mk = picker.querySelector('[data-ymm-make]');
    var md = picker.querySelector('[data-ymm-model]');
    var extra = '';
    if (y && y.value) extra += '&year=' + encodeURIComponent(y.value);
    if (mk && mk.value) extra += '&make=' + encodeURIComponent(mk.value);
    if (md && md.value) extra += '&model=' + encodeURIComponent(md.value);
    help.setAttribute('href', base + extra);
  }

  // Append options, preserving the placeholder (first child).
  function fillPickerSelect(select, items) {
    if (!select) return;
    while (select.children.length > 1) select.removeChild(select.lastChild);
    (items || []).forEach(function (val) {
      var opt = document.createElement('option');
      opt.value = val;
      opt.textContent = val;
      select.appendChild(opt);
    });
  }

  // Reset a downstream select to just its placeholder.
  function resetPickerSelect(select, placeholder) {
    if (!select) return;
    select.innerHTML = '';
    var opt = document.createElement('option');
    opt.value = '';
    opt.textContent = placeholder;
    select.appendChild(opt);
    select.value = '';
  }

  function revealPicker(statusEl, picker) {
    var fb = statusEl.querySelector('[data-fitment-fallback]');
    if (fb) fb.hidden = true;
    picker.hidden = false;
  }

  // Populate the Year select from the LOCAL oracle, then reveal the picker.
  // On any failure (no HHymm, empty oracle, fetch error) we leave the picker
  // hidden and the down-page-fitment-table fallback visible — never a dead UI.
  // Returns a Promise resolving true once the picker is populated + shown
  // (false when it stays hidden), so the Change action can focus it.
  function mountFitmentPicker(statusEl) {
    var picker = statusEl.querySelector('[data-fitment-picker]');
    if (!picker) return Promise.resolve(false);
    if (picker.dataset.ymmReady === 'true') { revealPicker(statusEl, picker); return Promise.resolve(true); }
    if (!window.HHymm || typeof window.HHymm.listYears !== 'function') return Promise.resolve(false);
    return window.HHymm.listYears().then(function (years) {
      if (!years || !years.length) return false; // oracle empty → keep the fallback
      fillPickerSelect(picker.querySelector('[data-ymm-year]'), years);
      picker.dataset.ymmReady = 'true';
      revealPicker(statusEl, picker);
      return true;
    }).catch(function (e) {
      if (window.console && console.warn) console.warn('[fitment-picker] populate failed:', e);
      return false;
    });
  }

  // Change · Clear — the ONE vehicle-action pair on every verdict that
  // names the buyer's vehicle (red-team F9 + F12, 2026-10-12). The fits
  // state used to carry a lone "Not your vehicle?" (which cleared), and the
  // no-fit state carried nothing, so a buyer told "Doesn't fit your 2008
  // Cadillac DTS" had no way to correct the vehicle from the verdict.
  //   Change → swaps the verdict for the in-buy-box picker, focused; the
  //            stored vehicle is untouched until a new one is picked (the
  //            picker's own HHymm.setStoredVehicle funnel).
  //   Clear  → HHymm.clearStoredVehicle (the funnel) → no-vehicle state.
  // opts.change === false drops Change (the no-data state: re-picking a vehicle
  // cannot produce a verdict for a part with no fitment data, so Clear only).
  function fitmentVehicleActionsHtml(opts) {
    if (opts && opts.change === false) {
      return '<span class="hh-pdp__fitment-actions">' +
        '<button type="button" class="hh-pdp__fitment-change" data-fitment-action="clear">Clear</button>' +
        '</span>';
    }
    return '<span class="hh-pdp__fitment-actions">' +
      '<button type="button" class="hh-pdp__fitment-change" data-fitment-action="change">Change</button>' +
      '<span aria-hidden="true"> · </span>' +
      '<button type="button" class="hh-pdp__fitment-change" data-fitment-action="clear">Clear</button>' +
      '</span>';
  }

  // Render the no-vehicle picker into the status element (state + markup).
  function showFitmentPicker(statusEl) {
    statusEl.classList.remove('hh-pdp__fitment--fits', 'hh-pdp__fitment--no-fit', 'hh-pdp__fitment--verify');
    statusEl.classList.add('hh-pdp__fitment--no-vehicle');
    statusEl.dataset.fitmentState = 'no-vehicle';
    statusEl.setAttribute('role', 'status');
    if (!statusEl.querySelector('[data-fitment-picker]')) {
      statusEl.innerHTML = buildNoVehiclePickerHtml(statusEl);
    }
    return mountFitmentPicker(statusEl);
  }

  // Delegated change handler for the picker cascade (attached once on the
  // status element; survives innerHTML rebuilds). Year → Make → Model → funnel.
  function onFitmentPickerChange(e) {
    var sel = e.target;
    if (!sel || sel.tagName !== 'SELECT') return;
    var picker = sel.closest('[data-fitment-picker]');
    if (!picker) return;
    var HH = window.HHymm;
    if (!HH) return;
    var statusEl = picker.closest('[data-fitment-status]');
    var yearSel = picker.querySelector('[data-ymm-year]');
    var makeSel = picker.querySelector('[data-ymm-make]');
    var modelSel = picker.querySelector('[data-ymm-model]');

    if (sel === yearSel) {
      resetPickerSelect(makeSel, 'Make'); makeSel.disabled = true;
      resetPickerSelect(modelSel, 'Model'); modelSel.disabled = true;
      updateYmmHelpHref(statusEl, picker); // carry the partial vehicle into the escape
      if (!yearSel.value) return;
      HH.listMakes(yearSel.value).then(function (makes) {
        fillPickerSelect(makeSel, makes);
        makeSel.disabled = false;
      }).catch(function () {});
    } else if (sel === makeSel) {
      resetPickerSelect(modelSel, 'Model'); modelSel.disabled = true;
      updateYmmHelpHref(statusEl, picker);
      if (!makeSel.value) return;
      HH.listModels(yearSel.value, makeSel.value).then(function (models) {
        fillPickerSelect(modelSel, models);
        modelSel.disabled = false;
      }).catch(function () {});
    } else if (sel === modelSel) {
      if (yearSel.value && makeSel.value && modelSel.value && HH.setStoredVehicle) {
        // THE funnel — fires hh:vehicle-changed → updateFitmentStatus re-renders
        // this element in place to the ✅/⚠️/❌ verdict.
        HH.setStoredVehicle({ year: yearSel.value, make: makeSel.value, model: modelSel.value });
      }
    }
  }

  // ---------------------------------------------------------------
  // updateFitmentStatus — the buy-box check-fit verdict (Leg B).
  //
  // Server renders the no-vehicle / universal state; this upgrades to the
  // full ✅/⚠️/❌ machine post-paint and on every hh:vehicle-changed.
  // The verdict LOGIC lives in HHymm.resolveVerdict (pure, unit-tested);
  // this function is DOM only. Universal is never overridden.
  // ---------------------------------------------------------------
  function updateFitmentStatus() {
    var statusEl = document.querySelector('[data-fitment-status]');
    if (!statusEl) return;
    if (statusEl.getAttribute('data-fitment-state') === 'universal') return; // never override universal
    if (!window.HHymm || !window.HHymm.resolveVerdict || !window.HHymm.verdictCopy) return;

    var fitment;
    try { fitment = JSON.parse(statusEl.getAttribute('data-product-fitment') || 'null'); } catch (err) { return; }
    // No fitment data is its own state ('no-data'), resolved below — it is NOT an
    // early return any more (GH #3433): with a stored vehicle the buyer is told the
    // fit is not listed, instead of being shown nothing.

    var exclusions = [];
    try { exclusions = JSON.parse(statusEl.getAttribute('data-product-exclusions') || '[]') || []; } catch (err) { exclusions = []; }

    var vehicle = window.HHymm.getStoredVehicle ? window.HHymm.getStoredVehicle() : null;
    var verdict = window.HHymm.resolveVerdict(vehicle, fitment, exclusions);
    var label = escapeHtml(verdict.label || '');

    var copy = copyFor(verdict);

    statusEl.classList.remove(
      'hh-pdp__fitment--no-vehicle',
      'hh-pdp__fitment--fits',
      'hh-pdp__fitment--no-fit',
      'hh-pdp__fitment--verify',
      'hh-pdp__fitment--no-data'
    );
    statusEl.setAttribute('role', 'status');

    if (statusEl.getAttribute('data-fitment-source') === 'none') {
      // The part carries no fitment data. No vehicle → render nothing (there is
      // nothing to check against, so no picker); a vehicle → the neutral state.
      if (verdict.state !== 'no-data') {
        statusEl.hidden = true;
        statusEl.dataset.fitmentState = 'no-data-hidden';
        statusEl.innerHTML = '';
        return;
      }
      statusEl.hidden = false;
      statusEl.classList.add('hh-pdp__fitment--no-data');
      statusEl.dataset.fitmentState = 'no-data';
      statusEl.innerHTML =
        '<span class="hh-pdp__fitment-line">' + ICON_INFO + ' <span>' + verdictCopyHtml(copy) + '</span></span>' +
        fitmentVehicleActionsHtml({ change: false });
      return;
    }

    if (verdict.state === 'no-vehicle') {
      // No-vehicle state — B4 LOCAL in-buy-box Y/M/M picker (ratified by Steve
      // 2026-07-06). On first paint the server-rendered picker is already in the
      // DOM (we just mount it). When we arrive here from another state (e.g. the
      // buyer cleared their vehicle), the innerHTML was replaced, so rebuild the
      // picker markup first. mountFitmentPicker populates the years from the
      // LOCAL ymm oracle (no API) and reveals it; the delegated change handler
      // wires the cascade → HHymm.setStoredVehicle (the funnel).
      showFitmentPicker(statusEl);
      return;
    }

    if (verdict.state === 'fits') {
      statusEl.classList.add('hh-pdp__fitment--fits');
      statusEl.dataset.fitmentState = 'fits';
      statusEl.innerHTML =
        '<span class="hh-pdp__fitment-line">' + ICON_CHECK + ' <span>' + verdictCopyHtml(copy) + '</span></span>' +
        fitmentVehicleActionsHtml();
      return;
    }

    if (verdict.state === 'verify') {
      // ⚠️ YMM matches but the part excludes a trim (e.g. Hybrid) and we don't
      // know the buyer's trim. The YMM picker is Y/M/M only, so we ask one
      // question; the answer persists via the HHymm funnel (never direct
      // localStorage). Yes → excluded (❌), No → fits (✅), Not sure → stay ⚠️.
      var exLabel = escapeHtml(exclusionLabel(verdict.exclusions));
      var exKey = escapeHtml((verdict.exclusions && verdict.exclusions[0]) || '');
      statusEl.classList.add('hh-pdp__fitment--verify');
      statusEl.dataset.fitmentState = 'verify';
      statusEl.setAttribute('role', 'alert');
      statusEl.innerHTML =
        '<div class="hh-pdp__fitment-line">' + ICON_ALERT + ' <span>' + verdictCopyHtml(copy) + '</span></div>' +
        '<p class="hh-pdp__fitment-sub">Your ' + label + ' matches this part, but it doesn’t fit the ' + exLabel + ' trim.</p>' +
        '<div class="hh-pdp__fitment-ask" data-fitment-exclusion="' + exKey + '">' +
          '<span class="hh-pdp__fitment-q">Is your ' + escapeHtml(vehicle && vehicle.model || 'vehicle') + ' a ' + exLabel + '?</span>' +
          '<span class="hh-pdp__fitment-choices">' +
            '<button type="button" class="hh-pdp__fitment-choice" data-fitment-action="trim-yes">Yes</button>' +
            '<button type="button" class="hh-pdp__fitment-choice" data-fitment-action="trim-no">No</button>' +
            '<button type="button" class="hh-pdp__fitment-choice" data-fitment-action="trim-unsure">Not sure</button>' +
          '</span>' +
        '</div>' +
        fitmentVehicleActionsHtml();
      return;
    }

    // 'no-fit' (YMM doesn't match) or 'excluded' (trim disclosed = excluded).
    // Both are ❌; both offer recovery to parts that fit the buyer's vehicle.
    var recoveryBase = statusEl.dataset.recoveryBase || '/collections/all'; // the Shop by vehicle surface (ruling C8)
    var recoveryUrl = buildRecoveryUrl(recoveryBase, vehicle || {});
    var headline = verdictCopyHtml(copy);
    statusEl.classList.add('hh-pdp__fitment--no-fit');
    statusEl.dataset.fitmentState = (verdict.state === 'excluded') ? 'excluded' : 'no-fit';
    statusEl.innerHTML =
      '<div class="hh-pdp__fitment-line">' + ICON_X + ' <span>' + headline + '</span></div>' +
      '<p class="hh-pdp__fitment-recovery">' +
        '<a href="' + escapeHtml(recoveryUrl) + '">Shop parts that fit your ' + escapeHtml(vehicleLabel(vehicle) || 'vehicle') + '</a>' +
        ' · <a href="#hh-pdp-fitment-heading">See all compatible vehicles</a>' +
      '</p>' +
      fitmentVehicleActionsHtml();
  }

  // Delegated click handler for the verdict's interactive affordances —
  // attached once; survives innerHTML rebuilds on each hh:vehicle-changed.
  function onFitmentClick(e) {
    var btn = e.target.closest('[data-fitment-action]');
    if (!btn) return;
    var action = btn.getAttribute('data-fitment-action');
    var HH = window.HHymm;
    if (!HH || !HH.getStoredVehicle) return;
    var vehicle = HH.getStoredVehicle();

    if (action === 'clear') {
      if (HH.clearStoredVehicle) HH.clearStoredVehicle(); // → hh:vehicle-changed → re-render no-vehicle
      return;
    }
    if (action === 'change') {
      var statusEl = btn.closest('[data-fitment-status]');
      if (!statusEl) return;
      statusEl.innerHTML = buildNoVehiclePickerHtml(statusEl); // fresh picker, then focus it
      showFitmentPicker(statusEl).then(function (shown) {
        var yr = statusEl.querySelector('[data-ymm-year]');
        if (shown && yr) yr.focus();
      });
      return;
    }
    if (!vehicle) return;

    if (action === 'trim-yes') {
      var ask = btn.closest('[data-fitment-exclusion]');
      var key = ask ? ask.getAttribute('data-fitment-exclusion') : '';
      vehicle.trim = key;                 // discloses the excluded trim → ❌
      if (HH.setStoredVehicle) HH.setStoredVehicle(vehicle);
    } else if (action === 'trim-no') {
      vehicle.trim = 'standard';          // confirmed NOT the excluded trim → ✅
      if (HH.setStoredVehicle) HH.setStoredVehicle(vehicle);
    } else if (action === 'trim-unsure') {
      // No write — reveal a VIN message-us path in place; verdict stays ⚠️.
      var ask2 = btn.closest('.hh-pdp__fitment-ask');
      if (ask2 && !ask2.querySelector('.hh-pdp__fitment-vin')) {
        var p = document.createElement('p');
        p.className = 'hh-pdp__fitment-vin';
        p.innerHTML = 'No problem — <a href="/pages/contact">message us your VIN</a> and we’ll confirm the trim before you order.';
        ask2.appendChild(p);
      }
    }
  }

  function initFitmentStatus() {
    var statusEl = document.querySelector('[data-fitment-status]');
    if (statusEl) {
      statusEl.addEventListener('click', onFitmentClick);
      statusEl.addEventListener('change', onFitmentPickerChange); // B4 picker cascade
    }
    updateFitmentStatus();
    document.addEventListener('hh:vehicle-changed', updateFitmentStatus);
  }

  // ---------------------------------------------------------------
  // YMM demand signal — fitment-enrichment F2c (2026-06-04)
  //
  // Every vehicle selection (any surface — all writers funnel through
  // HHymm.setStoredVehicle, which fires hh:vehicle-changed) pushes a
  // `ymm_select` dataLayer event. Forwarding to GA4 needs a Custom Event
  // trigger + GA4 Event tag in GTM-5QBZJ7RR (theme-owned container — the
  // Shopify Channel App does NOT auto-cover custom events; see CLAUDE.md
  // GA4 decision rule). Selections for combos with no matching products are
  // the SOURCING demand signal (what buyers want that we don't stock).
  // Clears (detail === null) are deliberately not tracked.
  //
  // ── WHY THERE ARE TWO PUSHES HERE (2026-08-24, GH #762 / #541) ──────
  // The GTM Custom Event shape below was the ONLY emission until today, and
  // container GTM-5QBZJ7RR does not convert that shape into a GA4 event —
  // the same defect the fireDataLayerEvent comment block above documents and
  // measured for view_cart. Re-measured on this event specifically:
  // `ymm_select` was ABSENT from the GA4 event list over 60 days while 20
  // other event names reported, including `blog_cta_click` at 8 events —
  // the low-volume control that proves the gtag path reaches GA4 and that
  // the probe can register a hit at this magnitude.
  //
  // So the selection is ALSO emitted through fireGaEvent (the gtag shape
  // with production evidence). No double-count risk: nothing else on the
  // site emits `ymm_select`, and Shopify's own pixel has no such event.
  //
  // EXPIRY CONDITION: if a GTM Custom Event trigger for `ymm_select` is ever
  // added in GTM-5QBZJ7RR, this starts double-counting and the fireGaEvent
  // call must be removed in the same change.
  function initYmmDemandSignal() {
    document.addEventListener('hh:vehicle-changed', function (e) {
      var v = e && e.detail;
      if (!v || !v.year || !v.make) return;
      var params = {
        ymm_year: String(v.year),
        ymm_make: String(v.make),
        ymm_model: String(v.model || '')
      };
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: 'ymm_select' }, params));
      // The path with production evidence — see the note above.
      fireGaEvent('ymm_select', params);
    });
  }

  // ---------------------------------------------------------------
  // Vehicle banner — cascade dropdowns + set/change/clear flows
  //
  // Reads from HHymm (ymm.js) for catalog Y/M/M data. All state mutations
  // funnel through HHymm.setStoredVehicle / clearStoredVehicle (locked
  // funnel pattern in CLAUDE.md). Banner UI listens to hh:vehicle-changed
  // for cross-surface state sync.
  //
  // Banner is opt-in via theme editor (decision 6 deferred-render); this
  // function no-ops when no [data-vehicle-banner] is on the page.
  // ---------------------------------------------------------------
  function initVehicleBanner() {
    var banner = document.querySelector('[data-vehicle-banner]');
    if (!banner) return;

    var form = banner.querySelector('[data-vehicle-banner-form]');
    var yearSelect = banner.querySelector('[data-ymm-year]');
    var makeSelect = banner.querySelector('[data-ymm-make]');
    var modelSelect = banner.querySelector('[data-ymm-model]');
    var submitBtn = banner.querySelector('[data-vehicle-banner-submit]');
    var stored = banner.querySelector('[data-vehicle-banner-stored]');
    var storedLabel = banner.querySelector('[data-vehicle-banner-label]');
    var changeBtn = banner.querySelector('[data-vehicle-banner-change]');
    var clearBtn = banner.querySelector('[data-vehicle-banner-clear]');
    var expandBtn = banner.querySelector('[data-vehicle-banner-expand]');
    if (!form || !yearSelect || !makeSelect || !modelSelect || !submitBtn) return;

    function fillSelect(select, items, currentValue) {
      // Preserve the placeholder option (first child); replace the rest.
      while (select.children.length > 1) select.removeChild(select.lastChild);
      items.forEach(function (val) {
        var opt = document.createElement('option');
        opt.value = val;
        opt.textContent = val;
        if (currentValue && String(val) === String(currentValue)) opt.selected = true;
        select.appendChild(opt);
      });
    }

    function checkSubmitState() {
      var ready = yearSelect.value && makeSelect.value && modelSelect.value;
      submitBtn.disabled = !ready;
      submitBtn.setAttribute('aria-disabled', ready ? 'false' : 'true');
    }

    async function populateYears() {
      var years = await window.HHymm.listYears();
      fillSelect(yearSelect, years);
    }

    async function populateMakes() {
      makeSelect.disabled = !yearSelect.value;
      modelSelect.disabled = true;
      // Reset model + make when year changes
      while (makeSelect.children.length > 1) makeSelect.removeChild(makeSelect.lastChild);
      while (modelSelect.children.length > 1) modelSelect.removeChild(modelSelect.lastChild);
      if (!yearSelect.value) { checkSubmitState(); return; }
      var makes = await window.HHymm.listMakes(yearSelect.value);
      fillSelect(makeSelect, makes);
      checkSubmitState();
    }

    async function populateModels() {
      modelSelect.disabled = !makeSelect.value;
      while (modelSelect.children.length > 1) modelSelect.removeChild(modelSelect.lastChild);
      if (!makeSelect.value) { checkSubmitState(); return; }
      var models = await window.HHymm.listModels(yearSelect.value, makeSelect.value);
      fillSelect(modelSelect, models);
      checkSubmitState();
    }

    function showStoredState(vehicle) {
      banner.classList.remove('hh-vehicle-banner--prominent', 'hh-vehicle-banner--collapsed');
      banner.classList.add('hh-vehicle-banner--stored');
      banner.dataset.state = 'stored';
      if (form) form.hidden = true;
      if (expandBtn) expandBtn.hidden = true;
      if (stored) stored.hidden = false;
      if (storedLabel) {
        storedLabel.textContent = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ');
      }
    }

    function showFormState() {
      var defaultState = banner.getAttribute('data-state') === 'collapsed' ? 'collapsed' : 'prominent';
      banner.classList.remove('hh-vehicle-banner--stored');
      banner.classList.add('hh-vehicle-banner--' + defaultState);
      banner.dataset.state = defaultState;
      if (stored) stored.hidden = true;
      if (defaultState === 'collapsed') {
        if (expandBtn) expandBtn.hidden = false;
        if (form) form.hidden = true;
      } else {
        if (expandBtn) expandBtn.hidden = true;
        if (form) form.hidden = false;
      }
      // Reset selections
      yearSelect.value = '';
      makeSelect.value = '';
      modelSelect.value = '';
      makeSelect.disabled = true;
      modelSelect.disabled = true;
      checkSubmitState();
    }

    // ── Wire events ──
    yearSelect.addEventListener('change', populateMakes);
    makeSelect.addEventListener('change', populateModels);
    modelSelect.addEventListener('change', checkSubmitState);

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (submitBtn.disabled) return;
      window.HHymm.setStoredVehicle({
        year: yearSelect.value,
        make: makeSelect.value,
        model: modelSelect.value,
      });
      // hh:vehicle-changed fires from HHymm; our listener below handles the UI swap.
    });

    if (expandBtn) {
      expandBtn.addEventListener('click', function () {
        expandBtn.hidden = true;
        if (form) form.hidden = false;
        // Focus first dropdown so the cascade is keyboard-actionable
        yearSelect.focus();
      });
    }

    if (changeBtn) changeBtn.addEventListener('click', showFormState);
    if (clearBtn) clearBtn.addEventListener('click', function () {
      window.HHymm.clearStoredVehicle();
      // Listener below handles the UI swap.
    });

    document.addEventListener('hh:vehicle-changed', function (e) {
      if (e.detail) showStoredState(e.detail);
      else showFormState();
    });

    // ── Sticky offset — runtime-detect sticky-header state ──
    var header = document.querySelector('.hh-header');
    var headerSticky = header && header.getAttribute('data-sticky') === 'true';
    if (headerSticky) {
      var headerHeight = header.offsetHeight;
      banner.style.setProperty('--hh-vehicle-banner-offset', headerHeight + 'px');
    } else {
      banner.style.setProperty('--hh-vehicle-banner-offset', '0');
    }
    banner.setAttribute('data-sticky', 'true');

    // ── Hide during header search focus ──
    var searchEl = document.querySelector('.hh-header__search');
    if (searchEl) {
      searchEl.addEventListener('focusin', function () { banner.dataset.hidden = 'true'; });
      searchEl.addEventListener('focusout', function () { banner.dataset.hidden = 'false'; });
    }

    // ── Initial state from stored vehicle ──
    var initial = window.HHymm.getStoredVehicle();
    if (initial) {
      showStoredState(initial);
    } else {
      // Populate the dropdowns even if collapsed — saves a re-fetch when user expands
      populateYears();
    }
  }

  // NOTE: the former initPdpFitmentLink() (scroll-to-banner + hide-when-no-banner)
  // was removed with B4. The no-vehicle state is now a self-sufficient LOCAL
  // picker, so there is no #hh-ymm CTA to wire and nothing to hide — hiding the
  // status was the exact B1 dead-end avoidance the picker replaces.

  // ── PDP related products (Phase 2g) ────────────────────────────────
  // Shopify's `recommendations` Liquid object only populates when the
  // ---------------------------------------------------------------
  // PDP-1 — Sticky ATC visibility (mobile-only)
  //
  // Same-form via DOM-proxy click: sticky button does NOT submit a
  // form; it triggers .hh-pdp__atc.click() which submits the canonical
  // {%- form 'product' -%} in pdp-info.liquid. Single source of truth
  // for variant + qty state.
  //
  // Visibility driven by IntersectionObserver on the inline ATC button.
  // When inline ATC is OUT of viewport → sticky shows. When IN viewport
  // → sticky hides. Show-when-out-of-view-only (no scroll-direction
  // logic — banked Gate D: feature-creep concern; revisit post-launch).
  //
  // Mobile-only: CSS @media (max-width: 720px) gates display: block.
  // Desktop has the inline ATC always in view due to right-column
  // sticky buybox layout; sticky bar redundant. JS still runs on
  // desktop but the bar stays display: none, so toggling data-visible
  // is harmless.
  //
  // Reduced-motion: CSS @media (prefers-reduced-motion: reduce) drops
  // the transform transition (snap show/hide) — JS doesn't need to
  // branch on this; CSS handles.
  // ---------------------------------------------------------------
  function initPdpStickyAtc () {
    var stickyEl = document.querySelector('[data-pdp-sticky-atc]');
    var stickyBtn = document.querySelector('[data-pdp-sticky-atc-btn]');
    var inlineAtc = document.querySelector('.hh-pdp__atc');
    if (!stickyEl || !stickyBtn || !inlineAtc) return;

    // DOM-proxy click: sticky button forwards to inline ATC.
    stickyBtn.addEventListener('click', function () {
      // Sync ARIA disabled state from inline ATC (e.g., sold-out).
      if (inlineAtc.disabled || inlineAtc.getAttribute('aria-disabled') === 'true') return;
      inlineAtc.click();
    });

    // Visibility logic — show only when inline ATC is ABOVE viewport
    // (buyer has scrolled past it). Hide when inline is IN viewport OR
    // BELOW viewport (buyer hasn't reached it yet).
    //
    // Defense-in-depth pattern (defensive against IntersectionObserver
    // edge cases):
    //  1. IntersectionObserver — primary trigger for state transitions
    //     during smooth scrolling.
    //  2. Scroll listener — backup trigger for instant scroll cases
    //     (e.g., window.scrollTo(0, 0) jumps from "above viewport" to
    //     "below viewport" without crossing threshold; observer may not
    //     fire because intersection state is false → false).
    //
    // Both paths run the same evaluator: boundingClientRect.bottom < 0
    // means inline is above viewport (scrolled past) → show sticky.
    // Mirror the inline button's state onto the sticky one: label, disabled,
    // busy (red-team F7, 2026-10-12). Before, only `disabled` was copied, and
    // only on scroll — so a sold-out PDP showed a full-blue "Add to Cart" in
    // the sticky bar. The server now renders the right state (Liquid) and
    // this keeps it right after a variant change or during "Adding…".
    function syncStickyFromInline () {
      var label = inlineAtc.textContent.trim();
      if (label && stickyBtn.textContent.trim() !== label) stickyBtn.textContent = label;
      var disabled = inlineAtc.disabled || inlineAtc.getAttribute('aria-disabled') === 'true';
      var busy = inlineAtc.getAttribute('aria-busy') === 'true';
      if (disabled) {
        stickyBtn.setAttribute('disabled', '');
        stickyBtn.setAttribute('aria-disabled', 'true');
      } else {
        stickyBtn.removeAttribute('disabled');
        stickyBtn.removeAttribute('aria-disabled');
      }
      if (busy) stickyBtn.setAttribute('aria-busy', 'true');
      else stickyBtn.removeAttribute('aria-busy');
    }

    function evaluateVisibility () {
      var rect = inlineAtc.getBoundingClientRect();
      var scrolledPast = rect.bottom < 0;
      stickyEl.setAttribute('data-visible', String(scrolledPast));
      stickyEl.setAttribute('aria-hidden', String(!scrolledPast));
      syncStickyFromInline();
    }

    if ('MutationObserver' in window) {
      new MutationObserver(syncStickyFromInline).observe(inlineAtc, {
        attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'aria-busy'],
        childList: true, characterData: true, subtree: true
      });
    }

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(evaluateVisibility, { threshold: [0, 0.1] });
      observer.observe(inlineAtc);
    }

    // rAF-throttled scroll listener — backup trigger.
    var scrollScheduled = false;
    window.addEventListener('scroll', function () {
      if (scrollScheduled) return;
      scrollScheduled = true;
      window.requestAnimationFrame(function () {
        evaluateVisibility();
        scrollScheduled = false;
      });
    }, { passive: true });

    // Initial evaluation (covers page-load state).
    evaluateVisibility();
  }

  // ---------------------------------------------------------------
  // buildVehicleFilterUrl (Phase 2 unified YMM filter, 2026-05-10)
  //
  // Single helper for context-aware vehicle-filter URL construction.
  // Used by:
  //   - initCartVehicleBar modal submit (cart drawer modal)
  //   - initYmmCard default onSubmit (cart-drawer empty YMM, hero YMM)
  //
  // Behavior:
  //   - On a /collections/* page: reload current collection with vehicle
  //     filter params (preserves browse context, surfaces filtered results).
  //   - On any other page: redirect to /collections/all with vehicle
  //     filter params — THE one "Shop by vehicle" result surface (GH #3434,
  //     ruling C8). It shows every part that fits, in stock or not; the
  //     in-stock toggle (snippets/collection-stock-toggle.liquid) adds
  //     filter.v.availability=1. It used to be all-in-stock-items, which
  //     hid out-of-stock fits and had no toggle to show them.
  //
  // URL filter params shape (Shopify Search & Discovery native):
  //   filter.p.m.custom.fitment_years_text — year value
  //   filter.p.m.custom.fitment_makes      — make value
  //   filter.p.m.custom.fitment_models     — model value
  //
  // Same shape as the collection sidebar Cymm picker (line ~1322), so all
  // YMM entry points produce identical URL state. Replaces the prior
  // localStorage-only flow that produced inconsistent client-side dim
  // behavior on the destination page.
  // ---------------------------------------------------------------
  function buildVehicleFilterUrl (vehicle, currentHref) {
    // currentHref optional override — defaults to window.location.href.
    // Test surface: pass an explicit URL to exercise behavior without
    // depending on Shopify storefront page navigation (eliminates flake
    // from rate-limited preview-theme loads). Production callers pass
    // no second arg and use live page state.
    var href = currentHref || window.location.href;
    if (!vehicle || !vehicle.year || !vehicle.make || !vehicle.model) {
      return href;
    }
    var origin = (typeof window !== 'undefined' && window.location && window.location.origin)
      ? window.location.origin
      : 'https://hubeshub.com';
    var basePath;
    try {
      basePath = new URL(href).pathname;
    } catch (e) {
      basePath = '/';
    }
    var onCollection = basePath.indexOf('/collections/') === 0;
    // The model param may carry MORE THAN ONE value: Shopify ORs repeated
    // values of one filter, and a Ram pick is spelled `1500` by the oracle but
    // `Ram 1500` in our fitment_models (GH #2985) — HHymm.modelFilterValues
    // owns that rule. `remove` clears every prior model value; `append` adds
    // each spelling.
    var modelValues = modelFilterValues(vehicle.make, vehicle.model);
    var fitmentChanges = {
      set: {
        'filter.p.m.custom.fitment_years_text': String(vehicle.year),
        'filter.p.m.custom.fitment_makes': String(vehicle.make)
      },
      remove: ['filter.p.m.custom.fitment_models'],
      append: modelValues.map(function (m) { return ['filter.p.m.custom.fitment_models', m]; })
    };
    if (onCollection) {
      // On a collection page: preserve other filter/sort params already in
      // URL (e.g. Part Type filter, sort_by) — the shared URL-state helper
      // owns that (and drops the preview-mode params).
      return buildCollectionUrl(fitmentChanges, href);
    }
    // Off-collection (homepage / PDP / search / etc.): fresh URL on the one
    // Shop by vehicle surface. No availability param: it defaults to show all.
    return buildCollectionUrl(fitmentChanges, new URL('/collections/all', origin).toString());
  }

  // ---------------------------------------------------------------
  // buildCollectionUrl — THE one URL-state helper for collection browse
  // state. Sort, filter submit, load-more pagination, the vehicle filter
  // (buildVehicleFilterUrl above) and the vehicle Clear all build through
  // it, so no surface can drop a param another surface wrote.
  //
  // Red-team F1 (2026-10-12): the sort handler built from
  // `new URL(form.action)` — the bare collection.url — so choosing a sort
  // silently dropped every filter.* param, including the buyer's vehicle.
  // Starting from the CURRENT URL and naming only the change is the fix.
  //
  //   changes.set      { key: value }  — '' / null / undefined deletes the key
  //   changes.remove   [key, …]        — deletes every value of each key
  //   changes.append   [[key, value]]  — multi-valued facets (applied last)
  //   changes.keepPage true keeps `page`; by default it is dropped, because
  //                    any change of browse state invalidates the offset
  //
  // Shopify preview-mode params (_ab/_fd/_sc) are dropped: not canonical.
  // ---------------------------------------------------------------
  // ---------------------------------------------------------------
  // Model spellings (GH #2985). The rule lives in HHymm (assets/ymm.js) —
  // these wrappers only guard against HHymm not having loaded, in which
  // case they fall back to the exact single spelling (today's behavior).
  //
  //   modelFilterValues  — every fitment_models facet value naming a pick
  //   intersectModelScope — keep an oracle model when ANY of its spellings
  //                         is in the collection's facet scope (a Ram 1500
  //                         collection's facet says `Ram 1500`; the oracle
  //                         says `1500`, so exact intersection emptied it)
  //   pickerModelValue   — the oracle spelling the <select> options carry
  // ---------------------------------------------------------------
  function modelFilterValues (make, model) {
    if (window.HHymm && typeof window.HHymm.modelFilterValues === 'function') {
      return window.HHymm.modelFilterValues(make, model);
    }
    return model ? [String(model)] : [];
  }

  function intersectModelScope (models, scope, make) {
    if (!scope || scope.length === 0) return models;
    var scopeSet = new Set(scope);
    return models.filter(function (m) {
      return modelFilterValues(make, m).some(function (v) { return scopeSet.has(String(v)); });
    });
  }

  function pickerModelValue (model, make) {
    if (window.HHymm && typeof window.HHymm.oracleModelSpelling === 'function') {
      return window.HHymm.oracleModelSpelling(model, make);
    }
    return model;
  }

  var FITMENT_PARAMS = [
    'filter.p.m.custom.fitment_years_text',
    'filter.p.m.custom.fitment_makes',
    'filter.p.m.custom.fitment_models'
  ];

  function buildCollectionUrl (changes, currentHref) {
    changes = changes || {};
    var url = new URL(currentHref || window.location.href, window.location.origin);
    ['_ab', '_fd', '_sc'].forEach(function (k) { url.searchParams.delete(k); });
    (changes.remove || []).forEach(function (k) { url.searchParams.delete(k); });
    var set = changes.set || {};
    Object.keys(set).forEach(function (k) {
      var v = set[k];
      if (v === null || v === undefined || v === '') url.searchParams.delete(k);
      else url.searchParams.set(k, String(v));
    });
    (changes.append || []).forEach(function (pair) {
      url.searchParams.append(pair[0], String(pair[1]));
    });
    if (!changes.keepPage) url.searchParams.delete('page');
    return url.toString();
  }
  // window.HHbuildVehicleFilterUrl — TEST-ONLY exposure.
  // Functional tests in tests/functional/ymm-unified-filter.test.js exercise
  // this helper via page.evaluate(), which can only see globals. THEME CODE
  // (other functions in this file, Liquid render paths) MUST NOT call the
  // global-scoped name. Use the lexically-scoped buildVehicleFilterUrl
  // identifier directly within this IIFE. Future-resistant: keeps the
  // public API surface narrow + prevents accidental coupling to global.
  if (typeof window !== 'undefined') {
    window.HHbuildVehicleFilterUrl = buildVehicleFilterUrl;
  }

  // ---------------------------------------------------------------
  // CART-1 — Vehicle context bar + per-line fitment warning + modal
  //
  // Vehicle bar (cart-vehicle-bar snippet) renders only when
  // HHymm.getStoredVehicle() resolves. Listens to hh:vehicle-changed
  // for re-render on vehicle updates (modal submit, future Phase 8
  // banner submit, clear).
  //
  // Per-line verdict: cart-drawer-line-item emits data-line-fitment +
  // data-line-exclusions (JSON) + data-line-universal. JS runs the PDP's
  // HHymm.resolveVerdict + HHymm.verdictCopy per line into
  // [data-cart-line-fit] (GH #3433 — it used HHymm.matches and a fixed
  // "You changed vehicles" line). Universal-fitment lines are skipped.
  //
  // Modal ([Change] flow): cart-scoped one-off (Gate A locked
  // 2026-05-03 — build for current consumers; abstract on second use).
  // YMM cascading dropdowns via HHymm.listYears / listMakes / listModels.
  // Submit calls HHymm.setStoredVehicle which auto-dispatches
  // hh:vehicle-changed; bar listener handles re-render.
  // ---------------------------------------------------------------
  function initCartVehicleBar () {
    var bars = document.querySelectorAll('[data-cart-vehicle-bar]');
    var modal = document.querySelector('[data-cart-vehicle-modal]');
    if (bars.length === 0 && !modal) return; // not on a cart surface

    function vehicleLabelFromObj (v) {
      if (!v) return '';
      return [v.year, v.make, v.model].filter(Boolean).join(' ');
    }

    function renderBars (vehicle) {
      var label = vehicleLabelFromObj(vehicle);
      // Re-query every time: a cart refresh replaces the bar node, and the NodeList captured at
      // init then points at detached elements (2026-10-13).
      document.querySelectorAll('[data-cart-vehicle-bar]').forEach(function (bar) {
        var nameEl = bar.querySelector('[data-cart-vehicle-name]');
        if (vehicle && label) {
          if (nameEl) nameEl.textContent = label;
          bar.dataset.vehicle = label;
          bar.removeAttribute('hidden');
        } else {
          if (nameEl) nameEl.textContent = '';
          bar.dataset.vehicle = '';
          bar.setAttribute('hidden', '');
        }
      });
    }

    // The PDP's verdict, per line (GH #3433): HHymm.resolveVerdict — trim
    // exclusions included — and HHymm.verdictCopy for the wording, so the cart
    // can never tell a buyer something the PDP would not. .hh-cart-line--warn is
    // kept for any tone that is not a plain fit or neutral (verify · no-fit).
    function setLineFit (line, verdict) {
      var el = line.querySelector('[data-cart-line-fit]');
      var copy = verdict ? copyFor(verdict) : null;
      line.classList.toggle('hh-cart-line--warn', !!copy && (copy.tone === 'verify' || copy.tone === 'no-fit'));
      line.dataset.fitState = verdict ? verdict.state : '';
      if (!el) return;
      if (!copy) {
        el.hidden = true;
        el.innerHTML = '';
        el.removeAttribute('data-fit-tone');
        return;
      }
      var icon = copy.tone === 'fits' ? ICON_CHECK : (copy.tone === 'neutral' ? ICON_INFO : (copy.tone === 'verify' ? ICON_ALERT : ICON_X));
      var more = '';
      if (copy.tone === 'verify' || copy.tone === 'no-fit') {
        var titleLink = line.querySelector('.hh-cart-line__title');
        var href = titleLink ? titleLink.getAttribute('href') : '';
        if (href) {
          more = ' <a class="hh-cart-line__fit-link" href="' + escapeHtml(href.split('#')[0]) + '#hh-pdp-fitment-heading">See compatible vehicles</a>';
        }
      }
      el.setAttribute('data-fit-tone', copy.tone);
      el.innerHTML = '<span class="hh-cart-line__fit-icon">' + icon + '</span><span>' + verdictCopyHtml(copy) + more + '</span>';
      el.hidden = false;
    }

    function evaluateLines (vehicle) {
      var HH = window.HHymm;
      var lines = document.querySelectorAll('.hh-cart-line');
      lines.forEach(function (line) {
        // Universal fitment, or no stored vehicle → nothing to say.
        if (line.dataset.lineUniversal === 'true' || !vehicle || !HH || typeof HH.resolveVerdict !== 'function') {
          setLineFit(line, null);
          return;
        }
        var fitment = null;
        var exclusions = [];
        var fitmentJson = line.getAttribute('data-line-fitment');
        if (fitmentJson) {
          try { fitment = JSON.parse(fitmentJson); } catch (err) { setLineFit(line, null); return; }
          try { exclusions = JSON.parse(line.getAttribute('data-line-exclusions') || '[]') || []; } catch (err) { exclusions = []; }
        }
        // No fitment data on the line → resolveVerdict returns 'no-data', the neutral state.
        setLineFit(line, HH.resolveVerdict(vehicle, fitment, exclusions));
      });
    }

    function refreshFromStorage () {
      var vehicle = (window.HHymm && window.HHymm.getStoredVehicle) ? window.HHymm.getStoredVehicle() : null;
      renderBars(vehicle);
      evaluateLines(vehicle);
    }
    // A drawer or /cart refresh swaps in server HTML: the bar ships `hidden` and lines ship without
    // --warn. Re-run, or a buyer who adds a part that does NOT fit sees no warning until a reload
    // (measured on production 2026-10-13).
    document.addEventListener('hh:cart-refreshed', refreshFromStorage);

    // [Change] click → open modal
    document.addEventListener('click', function (e) {
      var changeBtn = e.target.closest('[data-cart-vehicle-change]');
      if (changeBtn && modal) {
        openModal();
        return;
      }
      var clearBtn = e.target.closest('[data-cart-vehicle-clear]');
      if (clearBtn) {
        // Clear stored vehicle → fires hh:vehicle-changed → bar re-renders hidden
        if (window.HHymm && typeof window.HHymm.clearStoredVehicle === 'function') {
          window.HHymm.clearStoredVehicle();
        }
        return;
      }
      var closeBtn = e.target.closest('[data-cart-vehicle-modal-close]');
      if (closeBtn && modal) {
        closeModal();
      }
    });

    // Modal lifecycle
    // Closed = `inert` (red-team F5, 2026-10-12): the modal lives inside the
    // cart drawer, and while closed its year select, close and submit buttons
    // were still in the drawer's Tab sequence — three stops on invisible
    // controls. `inert` removes them from focus and the a11y tree at once.
    // Opened from OUTSIDE the drawer (the collection banner's Change) the
    // closed drawer is itself inert, which would leave the modal visible but
    // unusable — so it is moved to <body> first. It then traps Tab itself.
    var lastFocusedEl = null;
    function openModal () {
      if (!modal) return;
      lastFocusedEl = document.activeElement;
      if (modal.parentElement && modal.parentElement.closest('[inert]')) {
        document.body.appendChild(modal);
      }
      modal.removeAttribute('inert');
      modal.dataset.open = 'true';
      modal.setAttribute('aria-hidden', 'false');
      // Populate Year if not already populated
      ensureYearsPopulated();
      // Focus the first interactable
      var firstSelect = modal.querySelector('[data-cart-vehicle-year]');
      if (firstSelect) firstSelect.focus({ preventScroll: true });
    }
    function closeModal () {
      if (!modal) return;
      modal.dataset.open = 'false';
      modal.setAttribute('aria-hidden', 'true');
      modal.setAttribute('inert', '');
      if (lastFocusedEl && lastFocusedEl.focus) lastFocusedEl.focus({ preventScroll: true });
    }
    cartVehicleModal = { open: openModal, close: closeModal };

    if (modal) {
      modal.setAttribute('inert', ''); // belt-and-braces with the Liquid attribute
      modal.addEventListener('keydown', function (e) {
        if (e.key !== 'Tab' || modal.dataset.open !== 'true') return;
        var f = visibleFocusables(modal);
        if (!f.length) return;
        if (e.shiftKey && document.activeElement === f[0]) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && document.activeElement === f[f.length - 1]) { e.preventDefault(); f[0].focus(); }
      });
    }

    // Escape closes modal
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && modal && modal.dataset.open === 'true') closeModal();
    });

    // YMM cascading dropdowns
    // One in-flight fill, shared by every caller (2026-10-13): a flag set AFTER the await let two
    // concurrent callers both append, and the Year list showed every year twice.
    var yearsFill = null;
    function ensureYearsPopulated () {
      if (yearsFill) return yearsFill;
      if (!modal || !window.HHymm || !window.HHymm.listYears) return Promise.resolve();
      var sel = modal.querySelector('[data-cart-vehicle-year]');
      if (!sel) return Promise.resolve();
      yearsFill = window.HHymm.listYears().then(function (years) {
        years.forEach(function (y) {
          var opt = document.createElement('option');
          opt.value = String(y);
          opt.textContent = String(y);
          sel.appendChild(opt);
        });
      }).catch(function () { yearsFill = null; /* swallow; a later call retries */ });
      return yearsFill;
    }

    if (modal) {
      var yearSel = modal.querySelector('[data-cart-vehicle-year]');
      var makeSel = modal.querySelector('[data-cart-vehicle-make]');
      var modelSel = modal.querySelector('[data-cart-vehicle-model]');
      var submitBtn = modal.querySelector('[data-cart-vehicle-submit]');
      var form = modal.querySelector('[data-cart-vehicle-form]');

      function resetSelect (sel, placeholder) {
        if (!sel) return;
        sel.innerHTML = '';
        var opt = document.createElement('option');
        opt.value = '';
        opt.textContent = placeholder;
        sel.appendChild(opt);
        sel.disabled = true;
      }
      function populateSelect (sel, items) {
        if (!sel) return;
        items.forEach(function (item) {
          var opt = document.createElement('option');
          opt.value = String(item);
          opt.textContent = String(item);
          sel.appendChild(opt);
        });
        sel.disabled = false;
      }
      function updateSubmitState () {
        if (!submitBtn) return;
        var enabled = yearSel && makeSel && modelSel && yearSel.value && makeSel.value && modelSel.value;
        submitBtn.disabled = !enabled;
      }

      if (yearSel) yearSel.addEventListener('change', async function () {
        resetSelect(makeSel, 'Make');
        resetSelect(modelSel, 'Model');
        updateSubmitState();
        if (!yearSel.value) return;
        try {
          var makes = await window.HHymm.listMakes(parseInt(yearSel.value, 10));
          populateSelect(makeSel, makes);
        } catch (err) { /* swallow */ }
      });
      if (makeSel) makeSel.addEventListener('change', async function () {
        resetSelect(modelSel, 'Model');
        updateSubmitState();
        if (!makeSel.value) return;
        try {
          var models = await window.HHymm.listModels(parseInt(yearSel.value, 10), makeSel.value);
          populateSelect(modelSel, models);
        } catch (err) { /* swallow */ }
      });
      if (modelSel) modelSel.addEventListener('change', updateSubmitState);

      if (form) form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (!yearSel || !makeSel || !modelSel) return;
        var vehicle = { year: parseInt(yearSel.value, 10), make: makeSel.value, model: modelSel.value };
        if (!vehicle.year || !vehicle.make || !vehicle.model) return;
        if (window.HHymm && window.HHymm.setStoredVehicle) {
          window.HHymm.setStoredVehicle(vehicle); // auto-dispatches hh:vehicle-changed
        }
        closeModal();
        // STAYS IN THE CART (Steve, 2026-10-13). This modal only opens from the cart surfaces
        // (drawer + /cart), and its copy promises "We'll re-check fit on the items in your cart".
        // setStoredVehicle fires hh:vehicle-changed -> refreshFromStorage re-renders the bar and
        // re-evaluates every line's fit in place. From 2026-05-10 it redirected through
        // buildVehicleFilterUrl, which took a buyer OUT of checkout to browse; the other YMM
        // entry points (hero, empty-cart, collection picker) still route through it.
      });
    }

    // Subscribe to vehicle changes (modal submit, future banner submit, clear)
    document.addEventListener('hh:vehicle-changed', refreshFromStorage);

    // Initial state
    refreshFromStorage();
  }

  // ---------------------------------------------------------------
  // initYmmCard — generic cascading-dropdown YMM finder.
  //
  // Refactored from initCartEmptyYmm at H0 of Phase 4 v1 build (2026-05-03)
  // when Hero became the second consumer of this pattern. Markup extracted
  // to snippets/ymm-card.liquid in the same H0 commit.
  //
  // Lazy-populates years on first focus (avoids fetching on every page load
  // when buyer never engages). Cascading dropdowns: year → make → model.
  // On submit, sets stored vehicle via HHymm.setStoredVehicle and redirects
  // (default: buildVehicleFilterUrl's one Shop by vehicle surface,
  // /collections/all — GH #3434). Custom onSubmit callback overrides the
  // default for surfaces with surface-specific behavior.
  //
  // Selectors derived from opts.prefix:
  //   form          — [data-{prefix}-ymm]
  //   year select   — [data-{prefix}-year]
  //   make select   — [data-{prefix}-make]
  //   model select  — [data-{prefix}-model]
  //   submit button — [data-{prefix}-ymm-submit]
  //
  // Both current consumers (cart-empty + Hero) use the default onSubmit
  // behavior; the callback hook is present as a divergence-readiness
  // affordance (per Steve's H0 sign-off) but neither caller needs it today.
  // ---------------------------------------------------------------
  function initYmmCard (opts) {
    opts = opts || {};
    var prefix = opts.prefix;
    if (!prefix) return;
    var onSubmit = opts.onSubmit || function (vehicle) {
      if (window.HHymm && window.HHymm.setStoredVehicle) {
        window.HHymm.setStoredVehicle(vehicle);
      }
      // Phase 2 unified YMM filter (2026-05-10): redirect with URL filter
      // params (Shopify-native server-side filtering). Replaces prior
      // localStorage-only redirect that produced inconsistent client-side
      // dim behavior. Helper handles context-aware destination
      // (current collection vs /collections/all).
      window.location.href = buildVehicleFilterUrl(vehicle);
    };

    var forms = document.querySelectorAll('[data-' + prefix + '-ymm]');
    if (forms.length === 0) return;

    forms.forEach(function (form) {
      var yearSel = form.querySelector('[data-' + prefix + '-year]');
      var makeSel = form.querySelector('[data-' + prefix + '-make]');
      var modelSel = form.querySelector('[data-' + prefix + '-model]');
      var submitBtn = form.querySelector('[data-' + prefix + '-ymm-submit]');
      if (!yearSel || !makeSel || !modelSel) return;

      // Years populate EAGERLY at idle (2026-07-05 fix): the previous
      // lazy-on-first-focus pattern raced iOS — the native picker wheel opens
      // synchronously on tap and snapshots the near-empty select before the
      // oracle fetch resolves, so mobile buyers saw only the placeholder
      // (Steve, staging review). The oracle is ~11KB gz and shared with the
      // PDP picker, so eager idle population costs nearly nothing. Focus
      // remains as a fallback trigger.
      // eager idle population (see note above); focus stays as fallback. One in-flight fill shared
      // by both triggers (2026-10-13): a focus during the idle fill used to append the years twice.
      var yearsFill = null;
      (window.requestIdleCallback || function (f) { setTimeout(f, 200); })(function () { ensureYearsPopulated(); });
      function ensureYearsPopulated () {
        if (yearsFill) return yearsFill;
        if (!window.HHymm || !window.HHymm.listYears) return Promise.resolve();
        yearsFill = window.HHymm.listYears().then(function (years) {
          years.forEach(function (y) {
            var opt = document.createElement('option');
            opt.value = String(y);
            opt.textContent = String(y);
            yearSel.appendChild(opt);
          });
        }).catch(function () { yearsFill = null; });
        return yearsFill;
      }
      yearSel.addEventListener('focus', ensureYearsPopulated);

      function resetSelect (sel, placeholder) {
        sel.innerHTML = '';
        var opt = document.createElement('option');
        opt.value = '';
        opt.textContent = placeholder;
        sel.appendChild(opt);
        sel.disabled = true;
      }
      function populateSelect (sel, items) {
        items.forEach(function (item) {
          var opt = document.createElement('option');
          opt.value = String(item);
          opt.textContent = String(item);
          sel.appendChild(opt);
        });
        sel.disabled = false;
      }
      function updateSubmitState () {
        if (!submitBtn) return;
        submitBtn.disabled = !(yearSel.value && makeSel.value && modelSel.value);
      }

      yearSel.addEventListener('change', async function () {
        resetSelect(makeSel, 'Make');
        resetSelect(modelSel, 'Model');
        updateSubmitState();
        if (!yearSel.value || !window.HHymm || !window.HHymm.listMakes) return;
        try {
          var makes = await window.HHymm.listMakes(parseInt(yearSel.value, 10));
          populateSelect(makeSel, makes);
        } catch (err) { /* swallow */ }
      });
      makeSel.addEventListener('change', async function () {
        resetSelect(modelSel, 'Model');
        updateSubmitState();
        if (!makeSel.value || !window.HHymm || !window.HHymm.listModels) return;
        try {
          var models = await window.HHymm.listModels(parseInt(yearSel.value, 10), makeSel.value);
          populateSelect(modelSel, models);
        } catch (err) { /* swallow */ }
      });
      modelSel.addEventListener('change', updateSubmitState);

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var vehicle = { year: parseInt(yearSel.value, 10), make: makeSel.value, model: modelSel.value };
        if (!vehicle.year || !vehicle.make || !vehicle.model) return;
        onSubmit(vehicle);
      });

      // Pre-population path (SD-3-4 universal application — H3 lock 2026-05-03):
      // if a stored vehicle exists, eagerly populate cascading dropdowns and
      // select stored year/make/model. Benefits return visitors who land on
      // any surface consuming the shared snippet (Hero + cart-empty today;
      // future surfaces auto-inherit). Falls through silently to existing
      // lazy-populate-on-focus behavior on any failure (HHymm not yet ready,
      // empty fitment data, malformed stored vehicle, async error).
      //
      // Pattern 3 (defensive synchronous reads): try/catch fall-through;
      // existing lazy-populate-on-focus behavior is the silent fallback path.
      (async function prePopulateFromStoredVehicle () {
        try {
          if (!window.HHymm || !window.HHymm.getStoredVehicle) return;
          var stored = window.HHymm.getStoredVehicle();
          if (!stored || !stored.year || !stored.make || !stored.model) return;

          await ensureYearsPopulated();
          if (!yearSel.querySelector('option:not([value=""])')) return;
          yearSel.value = String(stored.year);

          var makes = await window.HHymm.listMakes(stored.year);
          if (!makes || makes.length === 0) return;
          populateSelect(makeSel, makes);
          makeSel.value = stored.make;

          var models = await window.HHymm.listModels(stored.year, stored.make);
          if (!models || models.length === 0) return;
          populateSelect(modelSel, models);
          modelSel.value = stored.model;

          updateSubmitState();
        } catch (err) { /* swallow; lazy-populate fallback remains for buyer */ }
      })();
    });
  }

  // ---------------------------------------------------------------
  // section is requested via Section Rendering API. On first paint the
  // section renders empty; this fetch populates it post-paint.
  // Below-the-fold, lazy — does not impact LCP.
  function initPdpRelated () {
    var el = document.querySelector('[data-pdp-related]');
    if (!el) return;
    // URL composed at runtime from split data-* attrs to avoid SEMrush
    // false-positive on "disallowed internal resource" — robots.txt blocks
    // /recommendations/products (correctly; SRA returns partial HTML, not
    // a page). Splitting keeps the URL string out of the DOM where the
    // SEMrush parser scans for internal resource links.
    // SRA URL contract: shopify.dev/docs/api/ajax/section-rendering
    var route = el.dataset.recommendationsRoute;
    var sid = el.dataset.sectionId;
    var pid = el.dataset.productId;
    var lim = el.dataset.relatedLimit || '6';
    if (!route || !sid || !pid) return;
    var url = route + '?section_id=' + encodeURIComponent(sid) +
              '&product_id=' + encodeURIComponent(pid) +
              '&intent=related&limit=' + encodeURIComponent(lim);
    fetch(url)
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-pdp-related]');
        if (fresh) el.innerHTML = fresh.innerHTML;
      })
      .catch(function () { /* silent — empty section is acceptable */ });
  }

  // ── Sold-out recovery instrumentation (2026-08-24) ──────────────────
  // 28.4% of PDP item views land on a product that was out of stock for the
  // whole window, and they convert at 0.16%. Three recovery paths exist on
  // those pages — notify-me, in-stock alternatives, "ask us to source" — and
  // NONE of them was measurable: GA4 carried zero notify/restock events of
  // any name since 2026-05-01, and /pages/request-a-part logged 2 views in
  // 28d with 0 tagged `source=pdp-sold-out`.
  //
  // Until this fires, "the recovery path does not work" and "the recovery
  // path is not instrumented" are indistinguishable, so the baseline is
  // stated as UNMEASURED rather than as a zero.
  //
  // Delegated on document because the notify-me control is injected by a
  // third party (Restock Rocket) AFTER load — a direct addEventListener at
  // init time would bind nothing and would look exactly like a working
  // listener that never fires.
  function initSoldOutRecoveryEvents () {
    var pdpRoot = document.querySelector('.hh-pdp');
    if (!pdpRoot) return;

    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;

      // 1. In-stock alternative click (our own markup).
      var alt = t.closest('[data-pdp-alt-link]');
      if (alt) {
        fireGaEvent('soldout_alternative_click', {
          alt_handle: alt.dataset.altHandle || '',
          alt_position: parseInt(alt.dataset.altPosition, 10) || 0,
          alt_origin: alt.dataset.altOrigin || '',
          source_handle: pdpRoot.dataset.productHandle || '',
          source_available: pdpRoot.dataset.productAvailable || ''
        });
        return;
      }

      // 2. "Ask us to source this exact part".
      var src = t.closest('[data-pdp-source-request]');
      if (src) {
        fireGaEvent('soldout_source_request_click', {
          source_handle: pdpRoot.dataset.productHandle || '',
          source_available: pdpRoot.dataset.productAvailable || ''
        });
        return;
      }

      // 3. Back-in-stock notify. Matched on the LIVE vendor's own class
      //    (Restock Rocket, verified 2026-08-24 on a live sold-out PDP) plus
      //    the legacy Stoq mount, so a vendor swap back does not silently
      //    stop the measurement. Class-based rather than text-based on
      //    purpose: button copy is vendor-configurable in an admin UI we do
      //    not control, so matching on copy would expire without warning.
      var notify = t.closest('.restock-rocket-button, [data-stoq-mount] button, .stoq-notify-me-button');
      if (notify) {
        fireGaEvent('soldout_notify_click', {
          vendor: t.closest('.restock-rocket-button') ? 'restock-rocket' : 'other',
          source_handle: pdpRoot.dataset.productHandle || '',
          source_available: pdpRoot.dataset.productAvailable || ''
        });
      }
    }, { passive: true });
  }

  // Diagnostic-video facade (video-assets Commit 2). Click-to-load: the PDP
  // renders a poster <button> in a 16:9 CSS-reserved box; on activation we
  // swap in a youtube-nocookie iframe. No above-the-fold third-party iframe →
  // no LCP/CLS hit (mirrors initPdpRelated discipline). Event delegation on
  // document so a <button> click — fired by mouse OR keyboard (Enter/Space) —
  // is caught regardless of when the markup paints.
  function initDiagnosticVideo () {
    document.addEventListener('click', function (ev) {
      var btn = ev.target.closest('[data-yt-facade]');
      if (!btn) return;
      var id = btn.dataset.ytId;
      if (!id) return;
      var iframe = document.createElement('iframe');
      iframe.className = 'hh-pdp-diag__iframe';
      iframe.src = 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(id) + '?autoplay=1&rel=0';
      iframe.title = btn.dataset.ytTitle || 'Diagnostic video';
      iframe.allow = 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture; web-share';
      iframe.setAttribute('allowfullscreen', '');
      iframe.loading = 'lazy';
      btn.replaceWith(iframe);
    });
  }

  // Collection pagination — ONE affordance per mode (red-team F4, 2026-10-12).
  //
  // The defect: "Show 24 more" appended page 2 and then history.pushState'd
  // to ?page=2. Back from a PDP re-rendered that URL — page 2's 24 cards and
  // nothing else — so the buyer lost the first 24 they had scrolled past. And
  // both affordances (numbered "1 2 →" and "Show 24 more") showed at once.
  //
  // Now:
  //   - NO-JS baseline: numbered pagination only (CSS hides "Show more" unless
  //     the nav carries data-hh-pager="more", which only JS sets).
  //   - JS, landing on page 1: "Show N more" only (the numbered list is
  //     hidden). Appending NEVER changes the URL's page — the URL stays the
  //     page-1 URL, which is what Back, refresh and share should show. How far
  //     the buyer had loaded is kept in history.state (hhLoadedThrough, plus
  //     hhScrollY), which the browser restores with the history entry; on
  //     load/Back, pages 2..N are re-fetched in order and the scroll position
  //     is put back. A bfcache restore keeps the DOM and needs nothing.
  //   - JS, landing mid-catalog (?page=3 from a search engine): numbered mode,
  //     because "load more" from page 3 can never reach pages 1-2.
  //
  // Page URLs are built by buildCollectionUrl (the shared URL-state helper),
  // so every filter and sort param rides along. SRA: section_id on the page
  // URL returns just this section; the server-rendered nav of page N decides
  // whether another "Show more" exists (button-hide on the last page).
  function pagerSectionId (nav) {
    return nav && nav.getAttribute('data-section-id');
  }

  function fetchCollectionPage (page, sectionId) {
    var url = buildCollectionUrl({ set: { page: page, section_id: sectionId }, keepPage: true });
    return fetch(url, { headers: { 'Accept': 'text/html' } })
      .then(function (r) { if (!r.ok) throw new Error('page ' + page + ' HTTP ' + r.status); return r.text(); })
      .then(function (html) { return new DOMParser().parseFromString(html, 'text/html'); });
  }

  // Append a fetched page's cards + swap in its nav. Returns the first
  // appended card (focus target) or null.
  function appendCollectionPage (doc) {
    var grid = document.querySelector('.hh-collection__grid');
    var newGrid = doc.querySelector('.hh-collection__grid');
    var first = null;
    if (grid && newGrid) {
      first = newGrid.firstElementChild;
      while (newGrid.firstElementChild) grid.appendChild(newGrid.firstElementChild);
    }
    var currentNav = document.querySelector('nav.hh-collection__pagination');
    var newNav = doc.querySelector('nav.hh-collection__pagination');
    if (currentNav && newNav) {
      newNav.setAttribute('data-hh-pager', 'more');
      currentNav.replaceWith(newNav);
    } else if (currentNav) {
      currentNav.remove();
    }
    return first;
  }

  function rememberPagerState (patch) {
    try {
      var state = Object.assign({}, window.history.state || {}, patch);
      window.history.replaceState(state, '', window.location.href);
    } catch (e) { /* state is an enhancement; never break browsing */ }
  }

  function initLoadMore () {
    var nav = document.querySelector('nav.hh-collection__pagination');
    if (!nav || !document.querySelector('.hh-collection__grid')) return;
    var startPage = parseInt(nav.getAttribute('data-current-page') || '1', 10) || 1;
    var sectionId = pagerSectionId(nav);
    if (startPage !== 1 || !sectionId) return; // numbered mode (see header)
    nav.setAttribute('data-hh-pager', 'more');

    var loadedThrough = 1;
    var scrollTimer = null;
    window.addEventListener('scroll', function () {
      if (loadedThrough < 2) return;
      if (scrollTimer) clearTimeout(scrollTimer);
      scrollTimer = setTimeout(function () { rememberPagerState({ hhScrollY: window.scrollY }); }, 200);
    }, { passive: true });

    // Restore pages 2..N after Back / refresh within the same history entry.
    var saved = window.history.state && window.history.state.hhLoadedThrough;
    if (saved && saved > 1) {
      if ('scrollRestoration' in window.history) window.history.scrollRestoration = 'manual';
      var pages = [];
      for (var p = 2; p <= saved; p++) pages.push(fetchCollectionPage(p, sectionId));
      Promise.all(pages).then(function (docs) {
        docs.forEach(appendCollectionPage);
        loadedThrough = saved;
        var y = window.history.state && window.history.state.hhScrollY;
        if (typeof y === 'number') window.scrollTo(0, y);
      }).catch(function () {
        // A page failed: keep page 1 rather than a gap, and forget the depth.
        rememberPagerState({ hhLoadedThrough: 1, hhScrollY: 0 });
      });
    }

    document.addEventListener('click', function (ev) {
      var btn = ev.target.closest('.hh-collection__load-more');
      if (!btn) return;
      // honor open-in-new-tab modifiers
      if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.button !== 0) return;
      var navNow = btn.closest('nav.hh-collection__pagination');
      if (!navNow || navNow.getAttribute('data-hh-pager') !== 'more') return;
      ev.preventDefault();
      if (btn.getAttribute('aria-busy') === 'true') return;
      btn.setAttribute('aria-busy', 'true');
      var next = loadedThrough + 1;
      fetchCollectionPage(next, sectionId)
        .then(function (doc) {
          var first = appendCollectionPage(doc);
          loadedThrough = next;
          rememberPagerState({ hhLoadedThrough: loadedThrough, hhScrollY: window.scrollY });
          // The button was replaced with the new nav — move focus to the first
          // new card instead of letting it fall to <body>.
          var target = first && (first.matches('a[href]') ? first : first.querySelector('a[href]'));
          if (target) target.focus({ preventScroll: true });
        })
        .catch(function () {
          // Fall back to the real page navigation the anchor always carried.
          window.location.href = btn.href;
        });
    });
  }

  // Collection filters — auto-submit on checkbox change (DESKTOP ONLY).
  // Form has method="get" + native checkbox semantics. Submitting the form
  // rebuilds the URL from currently-checked boxes; unchecked boxes are
  // correctly OMITTED. We do NOT build URLs by hand — see the "DO NOT switch
  // to JS-driven URL composition" comment in collection-filters.liquid.
  // No-JS fallback: form's <button type="submit"> visible when .no-js
  // remains on <html> (i.e., JS disabled or failed to run).
  //
  // Mobile (<1024px): batch + Apply pattern (3c-5 §3-A). Checkbox ticks
  // sit in form state until the visible Apply button submits. matchMedia
  // is read at change-event time, NOT cached at page load: buyers who
  // resize browser mid-session experience new behavior on next interaction
  // (responsive philosophy). Future maintainers: do NOT cache this — that
  // would break the responsive contract.
  //
  // Red-team F1 (2026-10-12): a native submit rebuilds the URL from the
  // form's controls ONLY, so any state the form does not carry (the vehicle
  // filter written by the YMM card, sort_by, a filter the rail does not
  // render) was dropped on every checkbox tick. The JS path now builds
  // through buildCollectionUrl: it removes exactly the param names this form
  // owns and re-appends what is checked, so unchecked boxes are still
  // OMITTED (no empty params) and everything else in the URL survives. The
  // Liquid also emits hidden inputs for that state, so no-JS keeps it too.
  function filterFormUrl (form) {
    var owned = {};
    Array.prototype.forEach.call(form.elements, function (el) {
      if (el.name) owned[el.name] = true;
    });
    var append = [];
    new FormData(form).forEach(function (value, key) {
      if (value !== '') append.push([key, value]);
    });
    return buildCollectionUrl({ remove: Object.keys(owned), append: append });
  }

  // Re-sync browse controls to the state the server rendered for THIS URL
  // (red-team F3, 2026-10-12). Back after ticking a filter restored the
  // unfiltered page from the back/forward cache — or re-rendered it and let
  // the browser restore form state — with the box the buyer had ticked
  // still checked: the URL said "no filter", the rail said "filtered".
  // defaultChecked / defaultSelected are the server's `checked` / `selected`
  // attributes, i.e. what Search & Discovery reports active for this URL, so
  // resetting to them is resetting to the URL (no value re-parsing, no
  // entity-escaping mismatch). Runs on every pageshow: persisted (bfcache)
  // and fresh loads alike. The forms also carry autocomplete="off".
  function resyncBrowseControls () {
    var boxes = document.querySelectorAll('form.hh-filters__form input[type="checkbox"]');
    Array.prototype.forEach.call(boxes, function (el) { el.checked = el.defaultChecked; });
    var sortSel = document.querySelector('form.hh-collection__sort select[name="sort_by"]');
    if (sortSel) {
      Array.prototype.forEach.call(sortSel.options, function (o) { o.selected = o.defaultSelected; });
    }
  }

  function initBrowseControlResync () {
    if (!document.querySelector('form.hh-filters__form, form.hh-collection__sort')) return;
    window.addEventListener('pageshow', resyncBrowseControls);
  }

  function initCollectionFilters () {
    var form = document.querySelector('form.hh-filters__form');
    if (!form) return;
    form.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || t.tagName !== 'INPUT' || t.type !== 'checkbox') return;
      var isDesktop = window.matchMedia('(min-width: 1024px)').matches;
      if (!isDesktop) return;  // mobile: batch+apply, don't auto-submit
      window.location.href = filterFormUrl(form);
    });
    // Mobile Apply (and desktop no-auto paths): same URL builder.
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      window.location.href = filterFormUrl(form);
    });
  }

  // Cymm — Collection-sidebar YMM picker (Phase 3 / Phase 9c-D, 2026-05-08).
  //
  // Atom spec: docs/design-source/2026-05-08-ymm-picker/Design - YMM Sidebar Picker.html
  // CSS: assets/coll-ymm.css
  // Liquid: snippets/collection-ymm-picker.liquid
  //
  // Replaces the flat Make / Model / Year facet checkboxes in the rail
  // with a cascading-dropdown picker pinned at the top of the sidebar.
  // Same URL params that the flat facets wrote (filter.p.m.custom.
  // fitment_makes / fitment_models / fitment_years_text) — only the UI
  // is replaced; Search & Discovery filtering is unchanged.
  //
  // Five render states (per spec):
  //   01 Empty       — Year active; Make/Model disabled; Apply+Clear disabled
  //   02 Cascade 1/3 — Year picked; Make active; Apply disabled, Clear enabled
  //   03 Cascade 2/3 — Year+Make picked; Model active; Apply still disabled
  //   04 Ready       — All three picked; Apply electric blue
  //   05 Locked      — Compact card; Edit / Clear vehicle actions
  //
  // Three entry vectors:
  //   - User lands on collection with no YMM URL params + no stored
  //     vehicle → render empty state
  //   - User lands with YMM URL params (server-side detection in the
  //     liquid) → render locked state directly
  //   - User lands with no URL params but stored vehicle exists in
  //     HHymm.getStoredVehicle() → JS swaps to locked state on mount
  //     (next click Apply re-applies URL params for current path)
  //
  // Edit transition: locked → cascade. Pre-fills the three selects with
  // the current vehicle. Visual differentiation handled by CSS swap of
  // .is-locked → cascade chrome (blue-tint, same as empty).
  //
  // Clear from locked: clears HHymm storage + strips YMM params from
  // current URL + reloads.
  function initCollectionYmmPicker () {
    var root = document.querySelector('[data-hh-cymm]');
    if (!root) return;
    if (!window.HHymm) return;

    var emptyEl = root.querySelector('[data-hh-cymm-empty]');
    var lockedEl = root.querySelector('[data-hh-cymm-locked]');
    var lockedVehEl = root.querySelector('[data-hh-cymm-locked-veh]');
    var yearSel = root.querySelector('[data-hh-cymm-year-select]');
    var makeSel = root.querySelector('[data-hh-cymm-make-select]');
    var modelSel = root.querySelector('[data-hh-cymm-model-select]');
    var applyBtn = root.querySelector('[data-hh-cymm-apply]');
    var clearCascadeBtn = root.querySelector('[data-hh-cymm-clear-cascade]');
    var editBtn = root.querySelector('[data-hh-cymm-edit]');
    var clearVehBtn = root.querySelector('[data-hh-cymm-clear-vehicle]');
    var savedEl = root.querySelector('[data-hh-cymm-saved]');
    var savedVehEl = root.querySelector('[data-hh-cymm-saved-veh]');
    var savedApplyBtn = root.querySelector('[data-hh-cymm-saved-apply]');
    var savedChangeBtn = root.querySelector('[data-hh-cymm-saved-change]');

    // ─── Collection-scoped cascade (Phase 3 follow-up, 2026-05-08) ──────
    // The picker calls HHymm.listYears/listMakes/listModels which load from
    // the GLOBAL fitment graph. On a make-specific or part-type-specific
    // collection (e.g. /collections/chrysler), the global list contains
    // makes that aren't in this collection — selecting them yields zero
    // results.
    //
    // Server-side, the liquid serialises collection.filters → fitment_*
    // values into JSON arrays on data-hh-cymm-scope-*. We intersect each
    // global list with the collection scope. If scope is empty (collection
    // has no fitment data — extreme edge case), fall back to the global
    // list to avoid catastrophic empty dropdowns.
    function parseScope (key) {
      try {
        var raw = root.dataset[key];
        if (!raw) return [];
        var arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr.map(String) : [];
      } catch (e) { return []; }
    }
    var scopeYears = parseScope('hhCymmScopeYears');
    var scopeMakes = parseScope('hhCymmScopeMakes');
    var scopeModels = parseScope('hhCymmScopeModels');

    function intersectScope (globalList, scope) {
      // Fallback: empty scope → return global (defense against catalog gaps)
      if (!scope || scope.length === 0) return globalList;
      var scopeSet = new Set(scope);
      return globalList.filter(function (it) { return scopeSet.has(String(it)); });
    }

    // ─── State helpers ────────────────────────────────────────────
    function showEmpty () {
      root.classList.remove('is-locked');
      lockedEl.hidden = true;
      if (savedEl) savedEl.hidden = true;
      emptyEl.hidden = false;
    }
    function showLocked (year, make, model) {
      root.classList.add('is-locked');
      emptyEl.hidden = true;
      if (savedEl) savedEl.hidden = true;
      lockedEl.hidden = false;
      lockedVehEl.textContent = year + ' ' + make + ' ' + model;
    }
    // Saved (F2): a stored vehicle the URL does NOT apply. Never the locked
    // "Showing parts for" card — that one is reserved for a filtered URL.
    function showSaved (year, make, model) {
      if (!savedEl) return;
      root.classList.remove('is-locked');
      emptyEl.hidden = true;
      lockedEl.hidden = true;
      savedEl.hidden = false;
      if (savedVehEl) savedVehEl.textContent = year + ' ' + make + ' ' + model;
    }
    function resetSelect (sel, placeholder) {
      sel.innerHTML = '';
      var opt = document.createElement('option');
      opt.value = '';
      opt.textContent = placeholder;
      sel.appendChild(opt);
      sel.disabled = true;
    }
    function populateSelect (sel, items, placeholder) {
      sel.innerHTML = '';
      var blank = document.createElement('option');
      blank.value = '';
      blank.textContent = placeholder;
      sel.appendChild(blank);
      items.forEach(function (it) {
        var opt = document.createElement('option');
        opt.value = String(it);
        opt.textContent = String(it);
        sel.appendChild(opt);
      });
      sel.disabled = false;
    }
    function updateApplyState () {
      var ready = !!(yearSel.value && makeSel.value && modelSel.value);
      applyBtn.disabled = !ready;
      applyBtn.setAttribute('aria-disabled', ready ? 'false' : 'true');
    }
    function updateClearState () {
      clearCascadeBtn.disabled = !(yearSel.value || makeSel.value || modelSel.value);
    }

    // ─── Cascade wiring ──────────────────────────────────────────
    // eager idle population — same iOS focus-race fix as initYmmCard (2026-07-05). One in-flight
    // fill shared by both triggers (2026-10-13): a focus during the idle fill appended years twice.
    var yearsFill = null;
    (window.requestIdleCallback || function (f) { setTimeout(f, 200); })(function () { ensureYearsPopulated(); });
    function ensureYearsPopulated () {
      if (yearsFill) return yearsFill;
      yearsFill = window.HHymm.listYears().then(function (years) {
        var scoped = intersectScope(years, scopeYears);
        var blank = yearSel.querySelector('option[value=""]');
        scoped.forEach(function (y) {
          var opt = document.createElement('option');
          opt.value = String(y);
          opt.textContent = String(y);
          yearSel.appendChild(opt);
        });
        if (blank) blank.textContent = 'Select year';
      }).catch(function () { yearsFill = null; });
      return yearsFill;
    }
    yearSel.addEventListener('focus', ensureYearsPopulated);

    yearSel.addEventListener('change', async function () {
      resetSelect(makeSel, 'Pick year first');
      resetSelect(modelSel, 'Pick make first');
      if (yearSel.value) {
        try {
          var makes = await window.HHymm.listMakes(parseInt(yearSel.value, 10));
          var scoped = intersectScope(makes, scopeMakes);
          populateSelect(makeSel, scoped, 'Select make');
        } catch (err) { /* swallow */ }
      }
      updateApplyState();
      updateClearState();
    });
    makeSel.addEventListener('change', async function () {
      resetSelect(modelSel, 'Pick make first');
      if (makeSel.value) {
        try {
          var models = await window.HHymm.listModels(parseInt(yearSel.value, 10), makeSel.value);
          var scoped = intersectModelScope(models, scopeModels, makeSel.value);
          populateSelect(modelSel, scoped, 'Select model');
        } catch (err) { /* swallow */ }
      }
      updateApplyState();
      updateClearState();
    });
    modelSel.addEventListener('change', function () {
      updateApplyState();
      updateClearState();
    });

    // ─── Apply ───────────────────────────────────────────────────
    applyBtn.addEventListener('click', function () {
      if (applyBtn.disabled) return;
      var year = parseInt(yearSel.value, 10);
      var make = makeSel.value;
      var model = modelSel.value;
      if (!year || !make || !model) return;
      var vehicle = { year: year, make: make, model: model };
      try {
        window.HHymm.setStoredVehicle(vehicle);
      } catch (err) { /* swallow */ }
      // Phase 2 unified YMM filter (2026-05-10): use shared helper for URL
      // construction. On collection pages this preserves current filter
      // state + drops Shopify preview-mode params; off-collection paths
      // route to /collections/all (GH #3434). Replaces inline URL build
      // that diverged on preview-param handling.
      window.location.assign(buildVehicleFilterUrl(vehicle));
    });

    // ─── Clear (cascade in-progress) ─────────────────────────────
    clearCascadeBtn.addEventListener('click', function () {
      yearSel.value = '';
      resetSelect(makeSel, 'Pick year first');
      resetSelect(modelSel, 'Pick make first');
      updateApplyState();
      updateClearState();
    });

    // ─── Change (locked or saved → cascade with values pre-filled) ──
    async function openCascadePrefilled () {
      var year = root.dataset.hhCymmYear;
      var make = root.dataset.hhCymmMake;
      var model = root.dataset.hhCymmModel;
      // Show cascade
      showEmpty();
      // Populate selects in order, pre-selecting each. Awaits keep cascade
      // dependencies correct: makes can't load until year is set.
      await ensureYearsPopulated();
      if (year) yearSel.value = year;
      if (year && make) {
        try {
          var makes = await window.HHymm.listMakes(parseInt(year, 10));
          var scopedMakes = intersectScope(makes, scopeMakes);
          populateSelect(makeSel, scopedMakes, 'Select make');
          makeSel.value = make;
        } catch (err) { /* swallow */ }
      }
      if (year && make && model) {
        try {
          var models = await window.HHymm.listModels(parseInt(year, 10), make);
          var scopedModels = intersectModelScope(models, scopeModels, make);
          populateSelect(modelSel, scopedModels, 'Select model');
          modelSel.value = pickerModelValue(model, make);
        } catch (err) { /* swallow */ }
      }
      updateApplyState();
      updateClearState();
      yearSel.focus();
    }
    editBtn.addEventListener('click', openCascadePrefilled);
    if (savedChangeBtn) savedChangeBtn.addEventListener('click', openCascadePrefilled);

    // ─── Apply a SAVED vehicle (F2) — same URL path as cascade Apply ──
    if (savedApplyBtn) {
      savedApplyBtn.addEventListener('click', function () {
        var vehicle = {
          year: root.dataset.hhCymmYear,
          make: root.dataset.hhCymmMake,
          model: root.dataset.hhCymmModel
        };
        if (!vehicle.year || !vehicle.make || !vehicle.model) return;
        window.location.assign(buildVehicleFilterUrl(vehicle));
      });
    }

    // ─── Clear vehicle (locked) ──────────────────────────────────
    clearVehBtn.addEventListener('click', function () {
      try {
        window.HHymm.clearStoredVehicle();
      } catch (err) { /* swallow */ }
      // Strip YMM params + reload (shared URL-state helper).
      window.location.assign(buildCollectionUrl({ remove: FITMENT_PARAMS }));
    });

    // ─── Hydrate-on-mount: the URL decides the state (F2) ────────
    // Red-team F2 (2026-10-12): a stored vehicle used to hydrate straight
    // into the locked card — "Filtering for your vehicle" — on a URL with
    // no fitment filter, i.e. over results identical to the unfiltered page.
    // resolveVehicleCardState() is the one rule; see its header.
    (function hydrateFromUrlOrStored () {
      if (root.classList.contains('is-locked')) return; // server-rendered locked
      try {
        var stored = window.HHymm.getStoredVehicle && window.HHymm.getStoredVehicle();
        var st = resolveVehicleCardState(vehicleFromUrl(), stored);
        if (st.state === 'empty') return;
        root.dataset.hhCymmYear = String(st.vehicle.year);
        root.dataset.hhCymmMake = st.vehicle.make;
        root.dataset.hhCymmModel = st.vehicle.model;
        if (st.state === 'applied') showLocked(st.vehicle.year, st.vehicle.make, st.vehicle.model);
        else showSaved(st.vehicle.year, st.vehicle.make, st.vehicle.model);
      } catch (err) { /* swallow */ }
    })();
  }

  // Symm — Search-rail YMM picker (Collections Phase 2, /search un-defer 2026-07-06).
  //
  // Search-scoped parallel of initCollectionYmmPicker. It is a SEPARATE init
  // (not a reuse) for one reason: Apply must MERGE the YMM facet into the
  // current /search URL, preserving `q` + `type` + any other applied filter —
  // whereas the collection picker's Apply routes off-page via
  // buildVehicleFilterUrl(), which treats /search as "off-collection" and
  // redirects to /collections/all, dropping the query. Binding
  // to data-hh-symm* (not data-hh-cymm*) keeps this fully additive — the
  // collection picker never touches this element, and this never touches the
  // collection element. Everything else (cascade, scope-intersection, locked
  // state, stored-vehicle hydration) mirrors the collection picker.
  //
  // Cascade/state helpers are intentionally duplicated rather than shared: the
  // collection picker must stay byte-identical (zero-regression bound), so the
  // two cannot fold into one function without editing the collection path.
  function initSearchYmmPicker () {
    var root = document.querySelector('[data-hh-symm]');
    if (!root) return;
    if (!window.HHymm) return;

    var emptyEl = root.querySelector('[data-hh-symm-empty]');
    var lockedEl = root.querySelector('[data-hh-symm-locked]');
    var lockedVehEl = root.querySelector('[data-hh-symm-locked-veh]');
    var yearSel = root.querySelector('[data-hh-symm-year-select]');
    var makeSel = root.querySelector('[data-hh-symm-make-select]');
    var modelSel = root.querySelector('[data-hh-symm-model-select]');
    var applyBtn = root.querySelector('[data-hh-symm-apply]');
    var clearCascadeBtn = root.querySelector('[data-hh-symm-clear-cascade]');
    var editBtn = root.querySelector('[data-hh-symm-edit]');
    var clearVehBtn = root.querySelector('[data-hh-symm-clear-vehicle]');
    var savedEl = root.querySelector('[data-hh-symm-saved]');
    var savedVehEl = root.querySelector('[data-hh-symm-saved-veh]');
    var savedApplyBtn = root.querySelector('[data-hh-symm-saved-apply]');
    var savedChangeBtn = root.querySelector('[data-hh-symm-saved-change]');
    if (!emptyEl || !lockedEl || !yearSel || !makeSel || !modelSel || !applyBtn) return;

    // Scope arrays serialise the facet's own value set (search.filters →
    // fitment_* values). Intersect each global list with the scope so the
    // cascade only offers values that exist in the current result set.
    function parseScope (key) {
      try {
        var raw = root.dataset[key];
        if (!raw) return [];
        var arr = JSON.parse(raw);
        return Array.isArray(arr) ? arr.map(String) : [];
      } catch (e) { return []; }
    }
    var scopeYears = parseScope('hhSymmScopeYears');
    var scopeMakes = parseScope('hhSymmScopeMakes');
    var scopeModels = parseScope('hhSymmScopeModels');

    function intersectScope (globalList, scope) {
      if (!scope || scope.length === 0) return globalList;
      var scopeSet = new Set(scope);
      return globalList.filter(function (it) { return scopeSet.has(String(it)); });
    }

    function showEmpty () {
      root.classList.remove('is-locked');
      lockedEl.hidden = true;
      if (savedEl) savedEl.hidden = true;
      emptyEl.hidden = false;
    }
    // Saved (F2) — mirror of the collection picker's showSaved.
    function showSaved (year, make, model) {
      if (!savedEl) return;
      root.classList.remove('is-locked');
      emptyEl.hidden = true;
      lockedEl.hidden = true;
      savedEl.hidden = false;
      if (savedVehEl) savedVehEl.textContent = year + ' ' + make + ' ' + model;
    }
    function showLocked (year, make, model) {
      root.classList.add('is-locked');
      emptyEl.hidden = true;
      if (savedEl) savedEl.hidden = true;
      lockedEl.hidden = false;
      if (lockedVehEl) lockedVehEl.textContent = year + ' ' + make + ' ' + model;
    }
    function resetSelect (sel, placeholder) {
      sel.innerHTML = '';
      var opt = document.createElement('option');
      opt.value = '';
      opt.textContent = placeholder;
      sel.appendChild(opt);
      sel.disabled = true;
    }
    function populateSelect (sel, items, placeholder) {
      sel.innerHTML = '';
      var blank = document.createElement('option');
      blank.value = '';
      blank.textContent = placeholder;
      sel.appendChild(blank);
      items.forEach(function (it) {
        var opt = document.createElement('option');
        opt.value = String(it);
        opt.textContent = String(it);
        sel.appendChild(opt);
      });
      sel.disabled = false;
    }
    function updateApplyState () {
      var ready = !!(yearSel.value && makeSel.value && modelSel.value);
      applyBtn.disabled = !ready;
      applyBtn.setAttribute('aria-disabled', ready ? 'false' : 'true');
    }
    function updateClearState () {
      if (clearCascadeBtn) clearCascadeBtn.disabled = !(yearSel.value || makeSel.value || modelSel.value);
    }

    // One in-flight fill shared by idle + focus (2026-10-13): see initCollectionYmmPicker.
    var yearsFill = null;
    (window.requestIdleCallback || function (f) { setTimeout(f, 200); })(function () { ensureYearsPopulated(); });
    function ensureYearsPopulated () {
      if (yearsFill) return yearsFill;
      yearsFill = window.HHymm.listYears().then(function (years) {
        var scoped = intersectScope(years, scopeYears);
        var blank = yearSel.querySelector('option[value=""]');
        scoped.forEach(function (y) {
          var opt = document.createElement('option');
          opt.value = String(y);
          opt.textContent = String(y);
          yearSel.appendChild(opt);
        });
        if (blank) blank.textContent = 'Select year';
      }).catch(function () { yearsFill = null; });
      return yearsFill;
    }
    yearSel.addEventListener('focus', ensureYearsPopulated);

    yearSel.addEventListener('change', async function () {
      resetSelect(makeSel, 'Pick year first');
      resetSelect(modelSel, 'Pick make first');
      if (yearSel.value) {
        try {
          var makes = await window.HHymm.listMakes(parseInt(yearSel.value, 10));
          populateSelect(makeSel, intersectScope(makes, scopeMakes), 'Select make');
        } catch (err) { /* swallow */ }
      }
      updateApplyState();
      updateClearState();
    });
    makeSel.addEventListener('change', async function () {
      resetSelect(modelSel, 'Pick make first');
      if (makeSel.value) {
        try {
          var models = await window.HHymm.listModels(parseInt(yearSel.value, 10), makeSel.value);
          populateSelect(modelSel, intersectModelScope(models, scopeModels, makeSel.value), 'Select model');
        } catch (err) { /* swallow */ }
      }
      updateApplyState();
      updateClearState();
    });
    modelSel.addEventListener('change', function () {
      updateApplyState();
      updateClearState();
    });

    // Apply — MERGE into the current /search URL (preserves q + type + other
    // filters + sort_by). This is the whole reason for a separate init.
    // Built through the shared URL-state helper (F1): everything already in
    // the /search URL survives; only type + the fitment params change.
    function searchVehicleUrl (year, make, model) {
      var set = { type: 'product' };
      set[FITMENT_PARAMS[0]] = String(year);
      set[FITMENT_PARAMS[1]] = make;
      set[FITMENT_PARAMS[2]] = model;
      return buildCollectionUrl({ set: set });
    }
    applyBtn.addEventListener('click', function () {
      if (applyBtn.disabled) return;
      var year = parseInt(yearSel.value, 10);
      var make = makeSel.value;
      var model = modelSel.value;
      if (!year || !make || !model) return;
      try { window.HHymm.setStoredVehicle({ year: year, make: make, model: model }); } catch (err) { /* swallow */ }
      window.location.assign(searchVehicleUrl(year, make, model));
    });
    if (savedApplyBtn) {
      savedApplyBtn.addEventListener('click', function () {
        var year = root.dataset.hhSymmYear;
        var make = root.dataset.hhSymmMake;
        var model = root.dataset.hhSymmModel;
        if (!year || !make || !model) return;
        window.location.assign(searchVehicleUrl(year, make, model));
      });
    }

    if (clearCascadeBtn) {
      clearCascadeBtn.addEventListener('click', function () {
        yearSel.value = '';
        resetSelect(makeSel, 'Pick year first');
        resetSelect(modelSel, 'Pick make first');
        updateApplyState();
        updateClearState();
      });
    }

    async function openCascadePrefilled () {
        var year = root.dataset.hhSymmYear;
        var make = root.dataset.hhSymmMake;
        var model = root.dataset.hhSymmModel;
        showEmpty();
        await ensureYearsPopulated();
        if (year) yearSel.value = year;
        if (year && make) {
          try {
            var makes = await window.HHymm.listMakes(parseInt(year, 10));
            populateSelect(makeSel, intersectScope(makes, scopeMakes), 'Select make');
            makeSel.value = make;
          } catch (err) { /* swallow */ }
        }
        if (year && make && model) {
          try {
            var models = await window.HHymm.listModels(parseInt(year, 10), make);
            populateSelect(modelSel, intersectModelScope(models, scopeModels, make), 'Select model');
            modelSel.value = pickerModelValue(model, make);
          } catch (err) { /* swallow */ }
        }
        updateApplyState();
        updateClearState();
        yearSel.focus();
    }
    if (editBtn) editBtn.addEventListener('click', openCascadePrefilled);
    if (savedChangeBtn) savedChangeBtn.addEventListener('click', openCascadePrefilled);

    // Clear vehicle (locked) — strip the YMM params, keep q + type, reload.
    if (clearVehBtn) {
      clearVehBtn.addEventListener('click', function () {
        try { window.HHymm.clearStoredVehicle(); } catch (err) { /* swallow */ }
        window.location.assign(buildCollectionUrl({ remove: FITMENT_PARAMS }));
      });
    }

    // Hydrate-on-mount: the URL decides the state (F2) — same rule as the
    // collection picker, resolveVehicleCardState().
    (function hydrateFromUrlOrStored () {
      if (root.classList.contains('is-locked')) return; // server-rendered locked
      try {
        var stored = window.HHymm.getStoredVehicle && window.HHymm.getStoredVehicle();
        var st = resolveVehicleCardState(vehicleFromUrl(), stored);
        if (st.state === 'empty') return;
        root.dataset.hhSymmYear = String(st.vehicle.year);
        root.dataset.hhSymmMake = st.vehicle.make;
        root.dataset.hhSymmModel = st.vehicle.model;
        if (st.state === 'applied') showLocked(st.vehicle.year, st.vehicle.make, st.vehicle.model);
        else showSaved(st.vehicle.year, st.vehicle.make, st.vehicle.model);
      } catch (err) { /* swallow */ }
    })();
  }

  // Spaced / dashed part-number fallback for full search (red-team F11,
  // 2026-10-12). Predictive search matched "25 759 726" and "YL3X-15045-D64";
  // the full /search page returned 0 for both while "25759726" and
  // "yl3x15045d64" found the part — the storefront search tokenizes on the
  // separators and ANDs the pieces. The theme cannot change how /search
  // matches (that is server-side Shopify search), so this is a client-side
  // RETRY, only on a zero-result page: if the query is PN-shaped with
  // separators, re-run it once with the separators stripped, and say so.
  //   zero results, PN-shaped q  → location.replace(q=<compact>&pn_from=<q>)
  //   pn_from page with results  → "No exact match for X — showing results for Y"
  //   pn_from page still zero    → show the buyer's ORIGINAL query in the
  //                                headings/inputs; no further retry (no loop)
  // Never fires when a filter caused the zero (initSearchStrandedFilterRecovery
  // owns that case), and never on a multi-word vehicle query.
  function compactPnQuery (q) {
    var raw = String(q == null ? '' : q).trim();
    if (raw.length > 40 || !/[\s-]/.test(raw)) return null;
    var tokens = raw.split(/[\s-]+/).filter(Boolean);
    if (tokens.length < 2) return null;
    var hasPnToken = false;
    for (var i = 0; i < tokens.length; i++) {
      var t = tokens[i];
      if (!/^[A-Za-z0-9]+$/.test(t)) return null;
      var digit = /\d/.test(t);
      if (!digit && t.length > 3) return null; // a word ("cadillac", "module") → not a PN
      if (digit && t.length >= 3) hasPnToken = true;
    }
    var compact = tokens.join('');
    if (!hasPnToken || compact.length < 7) return null;
    return compact;
  }

  function initSearchPnFallback () {
    var head = document.querySelector('.sr-head');
    if (!head) return; // not a performed search
    var url;
    try { url = new URL(window.location.href); } catch (e) { return; }
    var zero = document.querySelector('.sr-zero');
    var from = url.searchParams.get('pn_from');
    if (from) {
      if (zero) {
        // The compact retry found nothing either: describe the buyer's query.
        var quoted = '“' + from + '”';
        Array.prototype.forEach.call(document.querySelectorAll('.sr-query'), function (el) { el.textContent = '"' + from + '"'; });
        var h = zero.querySelector('.hh-empty__title');
        if (h) h.textContent = 'No results for ' + quoted + '.';
        Array.prototype.forEach.call(document.querySelectorAll('input[name="q"]'), function (el) { el.value = from; });
      } else if (!head.querySelector('[data-hh-pn-note]')) {
        var note = document.createElement('p');
        note.className = 'sr-meta';
        note.setAttribute('data-hh-pn-note', '');
        note.textContent = 'No exact match for “' + from + '” — showing results for “' +
          (url.searchParams.get('q') || '') + '”.';
        var meta = head.querySelector('.sr-meta');
        if (meta && meta.parentNode) meta.parentNode.insertBefore(note, meta.nextSibling);
        else head.appendChild(note);
      }
      return;
    }
    if (!zero) return;
    var q = url.searchParams.get('q');
    var filtered = Array.from(url.searchParams.keys()).some(function (k) { return k.indexOf('filter.') === 0; });
    if (!q || filtered) return;
    var compact = compactPnQuery(q);
    if (!compact || compact === q) return;
    window.location.replace(buildCollectionUrl({ set: { q: compact, pn_from: q } }));
  }

  // Search stranded-filter recovery (Collections Phase 2, /search un-defer).
  //
  // Mirror of initCollectionStrandedFilterRecovery for the /search zero-state.
  // When a filter narrows a search to zero products, Shopify's search.filters
  // active_values goes empty (same platform behavior the collection recovery
  // documents), so Liquid can't tell "empty query" from "filtered to nothing".
  // Liquid also can't read query strings. So at JS time we detect any
  // filter.* param in the URL on a zero-state page and inject a "Clear filters"
  // affordance that strips filters but keeps q + product scope.
  function initSearchStrandedFilterRecovery () {
    var zero = document.querySelector('.sr-zero');
    if (!zero) return;
    var url;
    try { url = new URL(window.location.href); } catch (e) { return; }
    var hasFilter = false;
    Array.prototype.forEach.call(Array.from(url.searchParams.keys()), function (k) {
      if (k.indexOf('filter.') === 0) hasFilter = true;
    });
    if (!hasFilter) return;

    // Build clear URL: drop every filter.* param + page; keep q; force product scope.
    var clear = new URL(url.href);
    Array.from(clear.searchParams.keys()).forEach(function (k) {
      if (k.indexOf('filter.') === 0) clear.searchParams.delete(k);
    });
    clear.searchParams.delete('page');
    clear.searchParams.set('type', 'product');

    // Avoid double-insert on re-init (defensive; init runs once per pageload).
    if (zero.querySelector('[data-hh-search-clear-filters]')) return;

    var wrap = document.createElement('div');
    wrap.className = 'sr-zero-clear';
    var link = document.createElement('a');
    // Reuse the existing zero-state CTA button visual (.sr-recover-cta-btn) —
    // no new button CSS; only the wrapper spacing is added (token-only).
    link.className = 'sr-recover-cta-btn hh-btn hh-btn--primary';
    link.setAttribute('data-hh-search-clear-filters', '');
    link.href = clear.pathname + clear.search;
    link.textContent = 'Clear filters and show all matches';
    wrap.appendChild(link);

    // Insert just under the zero-state heading so it reads as the primary
    // recovery action (a filter, not the query, caused the emptiness).
    var lead = zero.querySelector('.hh-empty__body');
    if (lead && lead.parentNode) {
      lead.parentNode.insertBefore(wrap, lead.nextSibling);
    } else {
      zero.insertBefore(wrap, zero.firstChild);
    }
  }

  // Filter show-more (Phase 2 Commit 3, 2026-05-08).
  // Non-YMM accordion groups (Part Type) cap visible options at 8; surplus
  // values render with class .hh-filters__option-row--overflow + the
  // `[data-overflow="true"]` attribute, hidden by CSS until JS adds
  // `.hh-filters__group--expanded` to the parent <details>. Click toggles
  // expanded state, updates aria-expanded + button label.
  //
  // Selector: [data-hh-filter-show-more] inside any .hh-filters__group.
  // No-JS fallback: button is rendered but inert; surplus values stay
  // hidden. Acceptable — JS-disabled buyers see the top 8 most common
  // values, which covers the vast majority of buyer searches by frequency.
  function initFilterShowMore () {
    var buttons = document.querySelectorAll('[data-hh-filter-show-more]');
    if (!buttons.length) return;
    Array.prototype.forEach.call(buttons, function (btn) {
      var group = btn.closest('.hh-filters__group');
      var hidden = btn.getAttribute('data-hidden-count') || '';
      btn.addEventListener('click', function () {
        var expanded = group.classList.toggle('hh-filters__group--expanded');
        btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        btn.textContent = expanded ? 'Show less' : 'Show ' + hidden + ' more';
      });
    });
  }

  // Filter drawer (3c-5) — mobile/tablet only.
  // Slide-from-right drawer triggered by [data-hh-filter-trigger]. Uses
  // position-fixed body scroll lock with scroll-position restoration
  // (canonical mobile-web pattern; avoids known overflow:hidden bugs on
  // iOS Safari). Focus trap inside drawer when open. ESC + scrim-click +
  // close-button + Apply-submit all close the drawer. Trigger receives
  // focus return on close.
  //
  // Drawer is the same <aside class="hh-filters"> that renders as a
  // static sidebar at ≥1024px. CSS @media handles the surface duality;
  // JS only manages the drawer-open/closed state attribute.
  //
  // No-JS fallback: trigger button is dead (CSS shows it but click does
  // nothing). Acceptable — JS-disabled mobile buyers see the same
  // sidebar-hidden layout as before 3c-5 (no regression vs 3c-2/3/4
  // mobile state). Don't over-engineer.
  function initCollectionFilterDrawer () {
    var trigger = document.querySelector('[data-hh-filter-trigger]');
    var aside = document.getElementById('hh-filter-drawer');
    var scrim = document.querySelector('[data-hh-filter-drawer-scrim]');
    var closeBtn = aside && aside.querySelector('[data-hh-filter-drawer-close]');
    if (!trigger || !aside) return;

    var savedScrollY = 0;

    function openDrawer () {
      // Save scroll position, lock body via position:fixed (iOS Safari-safe).
      savedScrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + savedScrollY + 'px';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      aside.setAttribute('data-open', 'true');
      if (scrim) {
        scrim.removeAttribute('hidden');
        scrim.setAttribute('data-open', 'true');
      }
      trigger.setAttribute('aria-expanded', 'true');
      // preventScroll: true required — default focus() behavior scrolls focused
      // element into view even when already visible, which conflicts with the
      // position-fixed scroll-lock restoration pattern. Without preventScroll,
      // window.scrollTo() in the close path is silently overridden by the
      // trigger.focus() side effect. Discovered at 3.5-2 audit; applies to all
      // drawer focus calls during open/close transitions.
      var firstFocusable = aside.querySelector('button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (firstFocusable) firstFocusable.focus({ preventScroll: true });
    }

    function closeDrawer () {
      // Restore body + scroll position, return focus to trigger.
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, savedScrollY);
      aside.setAttribute('data-open', 'false');
      if (scrim) {
        scrim.setAttribute('data-open', 'false');
        // Re-hide for the transition end; keep removable from a11y tree
        setTimeout(function () {
          if (scrim.getAttribute('data-open') === 'false') scrim.setAttribute('hidden', '');
        }, 250);
      }
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus({ preventScroll: true });
    }

    trigger.addEventListener('click', openDrawer);
    if (scrim) scrim.addEventListener('click', closeDrawer);
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && aside.getAttribute('data-open') === 'true') {
        e.preventDefault();
        closeDrawer();
      }
    });

    // Focus trap inside drawer when open. Tab cycles forward; Shift+Tab cycles back.
    aside.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      if (aside.getAttribute('data-open') !== 'true') return;
      var focusables = aside.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])');
      if (focusables.length === 0) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus({ preventScroll: true });
      }
    });
  }

  // Collection sort dropdown — auto-submit on change.
  // Featured (empty value) → strip sort_by + page from URL (clean URL).
  // Non-empty value → set sort_by, strip page (sort change resets pagination).
  // No JS fallback: native form submit works (Shopify treats empty sort_by as default;
  // page param naturally drops because the form has no hidden page input).
  // The no-JS form carries every active filter as a hidden input
  // (snippets/collection-sort.liquid), so it preserves filters too.
  //
  // Red-team F1 (2026-10-12): this used to build from new URL(form.action) —
  // the bare collection.url — and dropped every filter.* param, including the
  // vehicle filter. It now builds from the CURRENT URL via buildCollectionUrl.
  function initCollectionSort () {
    var form = document.querySelector('form.hh-collection__sort[data-hh-sort]');
    if (!form) return;
    var select = form.querySelector('select[name="sort_by"]');
    if (!select) return;
    select.addEventListener('change', function () {
      window.location.href = buildCollectionUrl({ set: { sort_by: select.value } });
    });
  }

  // ---------------------------------------------------------------
  // Collection vehicle banner (replaces Phase 3d card-dim, 2026-05-10)
  //
  // Surfaces stored vehicle on collection pages without URL filter
  // applied. Replaces the prior auto-dim-non-matching-cards behavior
  // with explicit Apply / Change / Clear affordances. See lesson #23
  // in docs/living/category-architecture-best-practices.md.
  //
  // Visibility rules:
  //   1. No stored vehicle → hidden (no banner ever)
  //   2. URL has any filter.p.m.custom.fitment_* param → hidden
  //      (filter is already active; banner would be redundant)
  //   3. Otherwise → visible with vehicle name + 3 actions
  //
  // Action wiring:
  //   Apply  → window.location.href = buildVehicleFilterUrl(vehicle, currentHref)
  //            (preserves current collection — does NOT redirect to
  //            /collections/all per the second-arg contract)
  //   Change → opens existing cart-vehicle-modal (reuse, don't duplicate)
  //   Clear  → HHymm.clearStoredVehicle() → fires hh:vehicle-changed →
  //            updateCollectionVehicleBanner re-renders to hidden state
  //
  // Reader of hh:vehicle-changed (per CLAUDE.md "YMM event flow funnel"
  // — writers funnel through HHymm.setStored / clearStoredVehicle,
  // readers listen to the dispatched event). Re-attach: full-page nav
  // on filter/sort/pagination triggers fresh DOMContentLoaded → init
  // runs again. No SRA-style re-attach needed (collection grid uses
  // native paginate, not SRA fetch).
  // ---------------------------------------------------------------
  function urlHasFitmentFilter () {
    try {
      var sp = new URL(window.location.href).searchParams;
      var keys = Array.from(sp.keys());
      for (var i = 0; i < keys.length; i++) {
        if (keys[i].indexOf('filter.p.m.custom.fitment_') === 0) return true;
      }
      return false;
    } catch (err) { return false; }
  }

  // The vehicle the URL applies, or null. All three fitment params are
  // required: a partial fitment URL does not name a vehicle.
  function vehicleFromUrl (href) {
    try {
      var sp = new URL(href || window.location.href, window.location.origin).searchParams;
      var year = sp.get(FITMENT_PARAMS[0]);
      var make = sp.get(FITMENT_PARAMS[1]);
      var model = sp.get(FITMENT_PARAMS[2]);
      if (!year || !make || !model) return null;
      return { year: year, make: make, model: model };
    } catch (e) { return null; }
  }

  // THE rule for a vehicle-context card (red-team F2, 2026-10-12). Exactly
  // two vehicle states, chosen by the URL — never by localStorage alone:
  //   URL applies a vehicle      → 'applied'  "Showing parts for X" · Change · Clear
  //   no URL vehicle, one stored → 'saved'    "Saved vehicle X" · Apply · Change
  //   neither                    → 'empty'
  // The URL vehicle wins over the stored one: the results on screen are the
  // URL's, so the card must describe the URL.
  function resolveVehicleCardState (urlVehicle, storedVehicle) {
    if (urlVehicle && urlVehicle.year && urlVehicle.make && urlVehicle.model) {
      return { state: 'applied', vehicle: urlVehicle };
    }
    if (storedVehicle && storedVehicle.year && storedVehicle.make && storedVehicle.model) {
      return { state: 'saved', vehicle: storedVehicle };
    }
    return { state: 'empty', vehicle: null };
  }

  function updateCollectionVehicleBanner () {
    var banners = document.querySelectorAll('[data-collection-vehicle-banner]');
    if (banners.length === 0) return;
    var vehicle = (window.HHymm && window.HHymm.getStoredVehicle) ? window.HHymm.getStoredVehicle() : null;
    var hasFilter = urlHasFitmentFilter();

    var label = vehicle ? [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ') : '';
    banners.forEach(function (banner) {
      var nameEl = banner.querySelector('[data-collection-vehicle-name]');
      if (vehicle && label && !hasFilter) {
        if (nameEl) nameEl.textContent = label;
        banner.dataset.vehicle = label;
        banner.removeAttribute('hidden');
      } else {
        if (nameEl) nameEl.textContent = '';
        banner.dataset.vehicle = '';
        banner.setAttribute('hidden', '');
      }
    });
  }

  function initCollectionVehicleBanner () {
    var banner = document.querySelector('[data-collection-vehicle-banner]');
    if (!banner) return; // not on a collection surface

    // Apply → redirect to filtered URL on current collection. Helper's
    // second arg pins destination to current href (vs default
    // /collections/all routing on non-collection pages).
    var applyBtn = banner.querySelector('[data-collection-vehicle-apply]');
    if (applyBtn) {
      applyBtn.addEventListener('click', function () {
        var vehicle = window.HHymm && window.HHymm.getStoredVehicle ? window.HHymm.getStoredVehicle() : null;
        if (!vehicle) return;
        window.location.href = buildVehicleFilterUrl(vehicle, window.location.href);
      });
    }

    // Change → open cart-vehicle-modal. Modal lives on every page (cart
    // drawer parent + standalone /cart) — same instance, reused here.
    // initCartVehicleBar wires the modal's lifecycle (open/close, focus,
    // YMM cascading dropdowns, submit→setStoredVehicle); we just trigger
    // the open. If the modal element isn't on the page (defensive) the
    // change button is a no-op rather than throwing.
    var changeBtn = banner.querySelector('[data-collection-vehicle-change]');
    if (changeBtn) {
      changeBtn.addEventListener('click', function () {
        // Shared opener (F5): un-inerts the modal and lifts it out of the
        // closed (inert) cart drawer. Setting data-open directly here left it
        // visible but unfocusable.
        if (cartVehicleModal) cartVehicleModal.open();
      });
    }

    // Clear → wipe stored vehicle. hh:vehicle-changed fires from
    // HHymm.clearStoredVehicle(); updateCollectionVehicleBanner listener
    // hides the banner via the no-vehicle branch.
    var clearBtn = banner.querySelector('[data-collection-vehicle-clear]');
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        if (window.HHymm && typeof window.HHymm.clearStoredVehicle === 'function') {
          window.HHymm.clearStoredVehicle();
        }
      });
    }

    updateCollectionVehicleBanner();
    document.addEventListener('hh:vehicle-changed', updateCollectionVehicleBanner);
  }

  // ---------------------------------------------------------------
  // Collection stranded-filter recovery (Phase 1 QA finding 3.A fix,
  // 2026-05-10; path (e) per Steve direction).
  //
  // When a buyer applies a fitment filter URL to a collection that
  // returns zero matching products, Shopify's collection.filters object
  // does NOT mark the filter as "active" (active_values stays empty)
  // because no product in the collection satisfies the filter. As a
  // result, the Liquid `has_active_filters` check returns false, and
  // the page falls into the `zero-products` empty state ("No parts
  // here yet.") — confusing because the category isn't empty; the
  // FILTER narrowed it to zero.
  //
  // Liquid can't see query strings (request.path doesn't expose them),
  // so the recovery has to happen at JS render time. We detect the
  // condition: zero-products empty state + URL contains any
  // filter.p.m.custom.fitment_* param, and rewrite the empty-state
  // copy + insert a "Clear all filters" link.
  //
  // Scoped to fitment_* params specifically (per Steve verification
  // request) — does NOT trigger on sort params, _ab/_fd/_sc preview
  // params, or non-fitment filters. Avoids confusing UX in scenarios
  // where URL has params but page is empty for non-filter reasons.
  // ---------------------------------------------------------------
  function initCollectionStrandedFilterRecovery () {
    var emptyState = document.querySelector('[data-hh-collection-empty="zero-products"]');
    if (!emptyState) return;

    // Scope-narrow detection: only trigger on fitment URL filter presence
    var hasFitmentFilter = false;
    try {
      var sp = new URL(window.location.href).searchParams;
      var keys = Array.from(sp.keys());
      for (var i = 0; i < keys.length; i++) {
        if (keys[i].indexOf('filter.p.m.custom.fitment_') === 0) { hasFitmentFilter = true; break; }
      }
    } catch (err) { return; }
    if (!hasFitmentFilter) return;

    // Rewrite headline + message (the EmptyState shell: snippets/empty-state.liquid)
    var title = emptyState.querySelector('.hh-empty__title');
    var message = emptyState.querySelector('.hh-empty__body');
    if (title) title.textContent = 'No parts match your filters.';
    if (message) message.textContent = 'Try removing a filter to see more results, or clear all filters to see everything in this category.';

    // Insert Clear-filters link before the existing Browse-all link
    var actions = emptyState.querySelector('.hh-empty__actions');
    if (actions) {
      var clearLink = document.createElement('a');
      clearLink.className = 'hh-btn hh-btn--primary';
      clearLink.href = window.location.pathname; // bare collection URL — strips ALL query params
      clearLink.textContent = 'Clear all filters';
      // Demote existing Browse link from --primary if it has that modifier (visual hierarchy preservation)
      var existingPrimary = actions.querySelector('.hh-btn--primary');
      if (existingPrimary) {
        existingPrimary.classList.remove('hh-btn--primary');
        existingPrimary.classList.add('hh-btn--secondary');
      }
      actions.insertBefore(clearLink, actions.firstChild);
    }
  }

  // ----- Cart drawer (3.5) -----
  // Slide-in cart panel triggered by header cart icon click. Mirrors
  // initCollectionFilterDrawer (3c-5) for scroll-lock + focus-trap +
  // ESC/outside-tap close. Differences from filter drawer:
  //   - Trigger is <a href="/cart">, not <button> — preventDefault on click
  //     when JS attaches; href is the no-JS fallback.
  //   - Both `aria-hidden` AND `inert` toggle in sync (Decision 8-A in plan).
  //   - Focus moves to close button on open (cart context: clearest exit).
  //   - Idempotency guard — protects against double-init edge case (parent
  //     plan §3 risk 3.5).
  // Subsequent sub-steps extend this function:
  //   3.5-2: AJAX add-to-cart from PDP form intercept; SRA re-render.
  //   3.5-3: qty stepper + remove handlers via /cart/change.js.
  //   3.5-5: GA4 view_cart event on openDrawer (with debounce).
  function initCartDrawer () {
    var aside = document.getElementById('hh-cart-drawer');
    var trigger = document.querySelector('[data-hh-cart-drawer-open]');
    var scrim = aside && aside.querySelector('[data-hh-cart-drawer-scrim]');
    var closeBtn = aside && aside.querySelector('[data-hh-cart-drawer-close]');
    if (!trigger || !aside) return;
    if (aside.dataset.hhInited === 'true') return;
    aside.dataset.hhInited = 'true';

    var savedScrollY = 0;
    // The control that opened the drawer (red-team F6, 2026-10-12): Escape /
    // close returns focus THERE — e.g. the PDP Add to Cart button — not
    // always to the header cart icon. Falls back to the trigger when the
    // opener is gone, disabled or unrendered.
    var lastOpener = null;

    function openDrawer (opener) {
      // Idempotent — re-calling on already-open drawer would overwrite savedScrollY
      // to 0 (body is position:fixed, so window.scrollY is 0), corrupting close-time
      // restoration. 3.5-2 introduces re-open scenarios (AJAX add while drawer open).
      if (aside.getAttribute('data-open') === 'true') return;
      lastOpener = (opener && typeof opener.focus === 'function') ? opener : null;
      // Save scroll position, lock body via position:fixed (iOS Safari-safe; mirrors filter drawer).
      savedScrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + savedScrollY + 'px';
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      aside.setAttribute('data-open', 'true');
      aside.removeAttribute('aria-hidden');
      aside.removeAttribute('inert');
      if (scrim) {
        scrim.removeAttribute('hidden');
        scrim.setAttribute('data-open', 'true');
      }
      trigger.setAttribute('aria-expanded', 'true');
      // preventScroll: true required — default focus() behavior scrolls focused
      // element into view even when already visible, which conflicts with the
      // position-fixed scroll-lock restoration pattern. Without preventScroll,
      // window.scrollTo() in the close path is silently overridden by the
      // trigger.focus() side effect. Discovered at 3.5-2 audit; applies to all
      // drawer focus calls during open/close transitions.
      // Focus the close button on open — clearest exit signal for cart context.
      if (closeBtn) closeBtn.focus({ preventScroll: true });

      // GA4 view_cart event on drawer open (3.5-5). Decision 1-A: single-fire
      // per pageview; viewCartFiredThisPageview gate skips duplicate when
      // /cart pageview already fired (initCartView path).
      if (!viewCartFiredThisPageview) {
        viewCartFiredThisPageview = true;
        var items = buildFullCartItems(aside);
        fireDataLayerEvent('view_cart', {
          currency: 'USD',
          value: readCartTotalFromDom(aside),
          items: items
        });
      }
    }

    function closeDrawer () {
      // Restore body + scroll position, return focus to trigger, re-mute a11y tree.
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, savedScrollY);
      aside.setAttribute('data-open', 'false');
      aside.setAttribute('aria-hidden', 'true');
      aside.setAttribute('inert', '');
      if (scrim) {
        scrim.setAttribute('data-open', 'false');
        // Re-hide for transition end; keeps scrim removable from a11y tree.
        // 250ms matches --hh-motion-drawer for in-sync visual + a11y completion.
        setTimeout(function () {
          if (scrim.getAttribute('data-open') === 'false') scrim.setAttribute('hidden', '');
        }, 250);
      }
      trigger.setAttribute('aria-expanded', 'false');
      var back = lastOpener;
      lastOpener = null;
      var usable = back && document.documentElement.contains(back) && !back.disabled && back.getClientRects().length > 0;
      (usable ? back : trigger).focus({ preventScroll: true });
    }

    // Trigger is <a href="/cart"> — preventDefault intercepts; href stays as no-JS fallback.
    trigger.addEventListener('click', function (e) {
      e.preventDefault();
      openDrawer(trigger);
    });
    if (scrim) scrim.addEventListener('click', closeDrawer);
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && aside.getAttribute('data-open') === 'true') {
        // An open vehicle modal owns this Escape (it closes itself); the
        // drawer underneath stays open. This listener is registered before
        // the modal's (initCartDrawer runs first), so it sees the modal open.
        if (document.querySelector('[data-cart-vehicle-modal][data-open="true"]')) return;
        e.preventDefault();
        closeDrawer();
      }
    });

    // Focus trap inside drawer when open. Tab cycles forward; Shift+Tab cycles back.
    // visibleFocusables() skips the closed vehicle modal (inert) and anything
    // unrendered — red-team F5: the trap's "last" element used to be one of the
    // closed modal's invisible controls.
    aside.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab') return;
      if (aside.getAttribute('data-open') !== 'true') return;
      var focusables = visibleFocusables(aside);
      if (focusables.length === 0) return;
      var first = focusables[0];
      var last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus({ preventScroll: true });
      }
    });

    // Expose drawer open/close to other module functions (3.5-2 AJAX add-to-cart,
    // 3.5-3 mutation handlers). Assigned post-listener-setup so consumers don't
    // race against handler attachment.
    cartDrawer = { open: openDrawer, close: closeDrawer };
  }

  // ----- AJAX add-to-cart (3.5-2) -----
  // Intercepts PDP form submit, fetches /cart/add.js, refreshes cart drawer
  // via SRA, syncs header count, opens drawer on success. JS-disabled buyers
  // fall back to native form-POST (action stays /cart/add → 302 to /cart, a
  // 404 until 3.5-4 — accepted gap per parent plan Risk 3.3).
  //
  // Subsequent sub-step extension hooks:
  //   3.5-3: cartDrawerRefresh() reused for /cart/change.js qty + remove
  //   3.5-4: cartDrawerRefresh() extends to /cart page section refresh
  //   3.5-5: GA4 dataLayer events fire at named comment hooks below

  // Focusable descendants a keyboard user can actually reach: not disabled,
  // not inside an `inert` subtree, and rendered (has client rects).
  function visibleFocusables (root) {
    var all = root.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])');
    return Array.prototype.filter.call(all, function (el) {
      return !el.closest('[inert]') && el.getClientRects().length > 0;
    });
  }

  // ----- Cart focus restore (red-team F5, 2026-10-12) -----
  // A qty +/- or Remove re-renders the line list (SRA innerHTML swap), which
  // destroys the focused control and drops focus to <body> — a keyboard or
  // screen-reader user is thrown to the top of the document mid-cart. The
  // intent is recorded at the moment of the click (before the control is
  // briefly disabled, which itself blurs it) and replayed after the refresh:
  // the SAME control on the SAME line (data-line-key is stable across qty
  // changes), else — the line is gone — the cart heading.
  var CART_FOCUS_SELECTORS = {
    inc: '[data-cart-qty-increment]',
    dec: '[data-cart-qty-decrement]',
    input: '[data-cart-qty-input]',
    remove: '[data-cart-line-remove]'
  };
  function cartFocusKind (el) {
    if (!el || !el.matches) return null;
    if (el.matches(CART_FOCUS_SELECTORS.inc)) return 'inc';
    if (el.matches(CART_FOCUS_SELECTORS.dec)) return 'dec';
    if (el.matches(CART_FOCUS_SELECTORS.input)) return 'input';
    if (el.matches(CART_FOCUS_SELECTORS.remove)) return 'remove';
    return null;
  }
  function restoreCartFocus (intent) {
    if (!intent) return;
    var active = document.activeElement;
    // The buyer already moved focus somewhere real — leave it there.
    if (active && active !== document.body && document.documentElement.contains(active)) return;
    var root = intent.onCartPage
      ? document.querySelector('[data-hh-cart-page]')
      : document.getElementById('hh-cart-drawer');
    if (!root) return;
    var line = null;
    Array.prototype.forEach.call(root.querySelectorAll('.hh-cart-line[data-line-key]'), function (l) {
      if (l.getAttribute('data-line-key') === intent.lineKey) line = l;
    });
    var target = null;
    if (line) {
      target = line.querySelector(CART_FOCUS_SELECTORS[intent.kind] || CART_FOCUS_SELECTORS.input);
      if (!target || target.disabled) target = line.querySelector(CART_FOCUS_SELECTORS.input);
    }
    if (!target) {
      target = intent.onCartPage ? root.querySelector('h1, h2') : root.querySelector('#hh-cart-drawer-title');
      if (target && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
    }
    if (target) target.focus({ preventScroll: true });
  }

  // SRA extraction couples to sections/cart-drawer.liquid panel layout:
  //   - .hh-cart-drawer__header (preserved; close button reference stays attached)
  //   - .hh-cart-drawer__error (preserved; managed separately by setError/clearError)
  //   - .hh-cart-drawer__items / .hh-cart-empty (replaced from SRA response)
  //   - .hh-cart-drawer__footer (replaced from SRA response)
  // If panel structure changes (Phase 4+), update extraction selectors here.
  function cartDrawerRefresh () {
    return fetch('/?section_id=cart-drawer', { headers: { 'Accept': 'text/html' } })
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var freshPanel = doc.querySelector('.hh-cart-drawer__panel');
        var currentPanel = document.querySelector('#hh-cart-drawer .hh-cart-drawer__panel');
        if (!freshPanel || !currentPanel) return;

        // Items list scrollTop preservation added at 3.5-3 — mutations within
        // drawer would lose scroll position without it. Initial 3.5-2
        // implementation didn't need this since AJAX add-from-PDP was the only
        // flow (drawer always opens fresh; no pre-existing scroll to preserve).
        var currentItems = currentPanel.querySelector('.hh-cart-drawer__items');
        var capturedScroll = currentItems ? currentItems.scrollTop : null;

        // Selective replacement: header + error region preserved (Decision 3-A
        // close-button reference + Decision 6-A error region survive refresh).
        var preservedClasses = ['hh-cart-drawer__header', 'hh-cart-drawer__error'];
        var toRemove = [];
        Array.prototype.forEach.call(currentPanel.children, function (child) {
          var classList = child.classList;
          var isPreserved = preservedClasses.some(function (c) { return classList.contains(c); });
          if (!isPreserved) toRemove.push(child);
        });
        toRemove.forEach(function (n) { n.remove(); });

        // Append fresh non-preserved children (cloneNode to detach from parsed doc)
        Array.prototype.forEach.call(freshPanel.children, function (child) {
          var classList = child.classList;
          var isPreserved = preservedClasses.some(function (c) { return classList.contains(c); });
          if (isPreserved) return;
          currentPanel.appendChild(child.cloneNode(true));
        });

        // Drawer header is preserved (close button reference + listener stays attached),
        // but the count span text inside it is stale (server rendered at page load).
        // Patch in fresh count text from SRA response so visible "(N)" + SR string update.
        // Without this, cartHeaderCountSync would read stale text and skip header sync.
        var freshCountEl = freshPanel.querySelector('[data-hh-cart-count]');
        var currentCountEl = currentPanel.querySelector('[data-hh-cart-count]');
        if (freshCountEl && currentCountEl) {
          currentCountEl.textContent = freshCountEl.textContent;
        }
        // SR count span is conditionally rendered (count > 0 only); insert if absent.
        var freshSrEl = freshPanel.querySelector('[data-hh-cart-count-sr]');
        var currentSrEl = currentPanel.querySelector('[data-hh-cart-count-sr]');
        if (freshSrEl && currentSrEl) {
          currentSrEl.textContent = freshSrEl.textContent;
        } else if (freshSrEl && !currentSrEl && currentCountEl) {
          // 0 → N transition: insert SR span after the visible count
          currentCountEl.parentNode.insertBefore(freshSrEl.cloneNode(true), currentCountEl.nextSibling);
        } else if (!freshSrEl && currentSrEl) {
          // N → 0 transition: remove SR span
          currentSrEl.remove();
        }

        cartHeaderCountSync();

        // Restore items scroll position. New items list may not exist if cart
        // became empty (last item removed → empty state rendered); guard for null.
        var newItems = currentPanel.querySelector('.hh-cart-drawer__items');
        if (newItems && capturedScroll != null) {
          newItems.scrollTop = capturedScroll;
        }
        document.dispatchEvent(new CustomEvent('hh:cart-refreshed'));
      });
  }

  // /cart page section refresh helper (3.5-4) — parallel to cartDrawerRefresh
  // for the /cart standalone page surface. Replaces the entire `[data-hh-cart-page]`
  // section content from SRA response. Called alongside cartDrawerRefresh on
  // /cart page mutations to preserve the drawer-as-source-of-truth invariant
  // for cartHeaderCountSync (3.5-2 invariant).
  //
  // No scroll preservation here: /cart page uses full-document scroll, not
  // internal items-list scroll. Browser preserves window.scrollY across DOM
  // mutations within <main>. If future layout adds internal scroll on items
  // column (e.g., sticky summary right column), carry forward
  // cartDrawerRefresh scroll-preservation pattern.
  function cartPageRefresh () {
    var current = document.querySelector('[data-hh-cart-page]');
    if (!current) return Promise.resolve();
    return fetch('/cart?section_id=main-cart', { headers: { 'Accept': 'text/html' } })
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('[data-hh-cart-page]');
        if (!fresh) return;
        // Replace entire section content; no preservation needed (no listener-bound
        // elements on /cart page — all listeners are document-delegated post-3.5-4).
        current.innerHTML = fresh.innerHTML;
        document.dispatchEvent(new CustomEvent('hh:cart-refreshed'));
      });
  }

  // Header badge structure mirrors sections/header.liquid:79-82:
  //   <span class="hh-header__cart-count" aria-hidden="true">N</span>
  //   <span class="visually-hidden">N items</span>
  // Keep in sync if header.liquid badge layout changes.
  function cartHeaderCountSync () {
    var drawerCountEl = document.querySelector('#hh-cart-drawer [data-hh-cart-count]');
    if (!drawerCountEl) return;
    // Drawer text format: "(3)" when count > 0, empty string when 0
    var match = drawerCountEl.textContent.match(/(\d+)/);
    var count = match ? parseInt(match[1], 10) : 0;

    var headerLink = document.querySelector('[data-hh-cart-drawer-open]');
    if (!headerLink) return;
    var existingBadge = headerLink.querySelector('.hh-header__cart-count');
    var existingSr = existingBadge && existingBadge.nextElementSibling;
    var existingSrIsHidden = existingSr && existingSr.classList.contains('visually-hidden');

    if (count === 0) {
      if (existingBadge) existingBadge.remove();
      if (existingSrIsHidden) existingSr.remove();
      return;
    }

    var srText = count + ' ' + (count === 1 ? 'item' : 'items');

    if (existingBadge) {
      existingBadge.textContent = String(count);
      if (existingSrIsHidden) existingSr.textContent = srText;
      return;
    }

    // 0 → N transition: insert badge structure
    var badge = document.createElement('span');
    badge.className = 'hh-header__cart-count';
    badge.setAttribute('aria-hidden', 'true');
    badge.textContent = String(count);
    var sr = document.createElement('span');
    sr.className = 'visually-hidden';
    sr.textContent = srText;
    headerLink.appendChild(badge);
    headerLink.appendChild(sr);
  }

  function initAjaxAddToCart () {
    var atcBtn = document.querySelector('[data-atc]:not([disabled])');
    if (!atcBtn) return;
    var form = atcBtn.closest('form');
    if (!form) return;
    if (form.dataset.hhAjaxAddInited === 'true') return;
    form.dataset.hhAjaxAddInited = 'true';

    // Per-form generation counter (closure-local). Each form on a page gets its
    // own counter; rapid clicks on the same form coalesce via button-disable +
    // counter check; cross-form races (Phase 4+ multi-ATC pages) stay isolated.
    var generationCounter = 0;

    var errorRegion = document.querySelector('#hh-cart-drawer [data-hh-cart-drawer-error]');

    function setError (message) {
      if (!errorRegion) return;
      errorRegion.textContent = message;
      errorRegion.removeAttribute('hidden');
    }
    function clearError () {
      if (!errorRegion) return;
      errorRegion.textContent = '';
      errorRegion.setAttribute('hidden', '');
    }

    // Loading state (red-team F6, 2026-10-12): the button used to disable
    // silently for ~0.65s with its label unchanged ("Add to Cart", grayed).
    // Now: label "Adding…", aria-busy, brand fill kept (CSS keys on
    // aria-busy). The label is restored from the server-rendered text.
    var atcLabel = atcBtn.textContent.trim();

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      clearError();
      var thisGeneration = ++generationCounter;
      // Who asked — captured BEFORE disabling (disabling a focused button
      // blurs it): the drawer returns focus here on Escape/close.
      var active = document.activeElement;
      var opener = (active && active !== document.body) ? active : atcBtn;
      atcBtn.disabled = true;
      atcBtn.setAttribute('aria-busy', 'true');
      atcBtn.textContent = 'Adding…';

      fetch('/cart/add.js', {
        method: 'POST',
        body: new FormData(form),
        headers: { 'Accept': 'application/json' }
      }).then(function (res) {
        return res.json().then(function (body) { return { ok: res.ok, body: body }; });
      }).then(function (result) {
        if (thisGeneration !== generationCounter) return;
        if (!result.ok) {
          var msg = (result.body && (result.body.description || result.body.message)) || 'Could not add to cart. Try again.';
          setError(msg);
          // Decision 6-A: drawer auto-opens on error path to surface the alert
          if (cartDrawer) cartDrawer.open(opener);
          return;
        }
        // GA4 add_to_cart event (3.5-5). Source data from /cart/add.js response.
        // Decision 4-A: vendor sourced from response (server-authoritative).
        var added = result.body || {};
        var addedItem = {
          item_id: String(added.product_id || added.id || ''),
          item_name: added.product_title || added.title || '',
          item_brand: added.vendor || '',
          price: (added.final_price || added.price || 0) / 100,
          quantity: added.quantity || 1
        };
        fireDataLayerEvent('add_to_cart', {
          currency: 'USD',
          value: addedItem.price * addedItem.quantity,
          items: [addedItem]
        });
        return cartDrawerRefresh().then(function () {
          if (thisGeneration !== generationCounter) return;
          if (cartDrawer) cartDrawer.open(opener);
        });
      }).catch(function () {
        if (thisGeneration !== generationCounter) return;
        setError('Network error. Try again.');
        if (cartDrawer) cartDrawer.open(opener);
      }).then(function () {
        if (thisGeneration !== generationCounter) return;
        atcBtn.disabled = false;
        atcBtn.removeAttribute('aria-busy');
        atcBtn.textContent = atcLabel;
      });
    });
  }

  // ----- Cart mutation handlers (3.5-3) -----
  // Qty stepper (+/−), direct qty input edit, and remove button — all bind to
  // the inert DOM hooks reserved at 3.5-1 and mutate via /cart/change.js.
  // cartDrawerRefresh from 3.5-2 reused for post-mutation section render +
  // header count sync; cartDrawer module reference reused for drawer.open()
  // (drawer assumed already open since mutations are user-initiated from
  // inside the drawer; but defensive .open() ensures error/clamp cases surface
  // even if buyer somehow triggered a mutation while drawer was closed).
  //
  // Stepper math intentionally duplicated from initQty (PDP) per 3.5-3 plan §5
  // Decision 1. PDP qty is form-side prep; cart qty is server-mutation trigger
  // — same math, different meaning. Distinct selectors (data-cart-qty-* vs
  // data-qty-*) keep grep precise. If future code adds a third stepper context
  // (Phase 4+ homepage quick-add, order-edit page), revisit shared abstraction
  // with empirical knowledge of all three contexts.
  function initCartMutations () {
    var drawer = document.getElementById('hh-cart-drawer');
    if (!drawer) return;
    if (drawer.dataset.hhMutationsInited === 'true') return;
    drawer.dataset.hhMutationsInited = 'true';

    var errorRegion = drawer.querySelector('[data-hh-cart-drawer-error]');

    function setError (message) {
      if (!errorRegion) return;
      errorRegion.textContent = message;
      errorRegion.removeAttribute('hidden');
    }
    function clearError () {
      if (!errorRegion) return;
      errorRegion.textContent = '';
      errorRegion.setAttribute('hidden', '');
    }

    // Per-line generation counter — closure-local Map keyed by line key.
    // Each mutation increments its line's counter; only the latest fetch wins
    // post-response handling. Protects under concurrent mutations on different
    // lines (line A in-flight + line B mutated → both end at correct state)
    // AND same-line races (+ then immediately remove on same line → remove wins).
    var generations = new Map();

    // Mutation helper — fetch /cart/change.js, gate on per-line generation,
    // dispatch to cartDrawerRefresh on success, surface inline error otherwise.
    // requestedQuantity passed so we can detect server clamp (response qty < requested).
    //
    // 3.5-4: routes refresh based on mutation context (ancestor walk per
    // Decision 5-A). On drawer: cartDrawerRefresh only. On /cart page: both
    // cartPageRefresh AND cartDrawerRefresh — drawer's count span is the
    // 3.5-2-locked source-of-truth for cartHeaderCountSync; refreshing both
    // preserves that invariant. Drawer fetch is cheap; single-fetch
    // optimization via multi-section query string logged for Phase 9.
    function refreshForContext (lineEl) {
      var onCartPage = lineEl && lineEl.closest('[data-hh-cart-page]');
      if (onCartPage) {
        return Promise.all([cartPageRefresh(), cartDrawerRefresh()]);
      }
      return cartDrawerRefresh();
    }

    // lineKey → focus intent recorded at click time (see restoreCartFocus).
    var focusIntents = new Map();
    function recordFocusIntent (lineKey, kind, lineEl) {
      if (!kind) return;
      focusIntents.set(lineKey, {
        lineKey: lineKey,
        kind: kind,
        onCartPage: !!(lineEl && lineEl.closest('[data-hh-cart-page]'))
      });
    }

    function cartLineUpdate (lineKey, requestedQuantity, lineEl) {
      clearError();
      var thisGeneration = (generations.get(lineKey) || 0) + 1;
      generations.set(lineKey, thisGeneration);
      if (lineEl) lineEl.setAttribute('aria-busy', 'true');

      // Capture pre-mutation line item data (Decision 5-A) — needed for
      // remove_from_cart event payload. By the time /cart/change.js returns
      // for a quantity=0 mutation, the response body's items[] no longer
      // contains the removed line.
      var preMutationItem = (requestedQuantity === 0 && lineEl) ? buildCartItemFromLine(lineEl) : null;

      return fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: lineKey, quantity: requestedQuantity })
      }).then(function (res) {
        return res.json().then(function (body) { return { ok: res.ok, body: body }; });
      }).then(function (result) {
        if (generations.get(lineKey) !== thisGeneration) return;
        if (!result.ok) {
          var msg = (result.body && (result.body.description || result.body.message)) || 'Could not update cart. Try again.';
          setError(msg);
          if (cartDrawer) cartDrawer.open();
          // Refresh drawer to restore input/qty to server-truth — without this,
          // the user-typed value (e.g. "5") sticks in the input while server
          // cart still shows the prior qty. Drawer state diverging from server
          // state is the bug class caught at 3.5-3 audit (Shopify returns 422
          // for stock-exceeded change requests; SRA refresh re-renders the
          // input with actual server value).
          return refreshForContext(lineEl).then(function () {
            restoreCartFocus(focusIntents.get(lineKey));
          });
        }
        // Inventory clamp detection: response.quantity < requested means server
        // reduced qty due to insufficient stock. Surface inline (200 path, not
        // 422 — different signal than 3.5-2's hard-error path).
        // Find the line in the response: response.items is an array; match by key.
        if (requestedQuantity > 0 && result.body && result.body.items) {
          var responseLine = result.body.items.find(function (i) { return i.key === lineKey; });
          if (responseLine && responseLine.quantity < requestedQuantity) {
            setError('Only ' + responseLine.quantity + ' available — quantity adjusted.');
          }
        }
        // GA4 remove_from_cart event (3.5-5) when requestedQuantity === 0.
        // Uses pre-mutation item data captured at start of cartLineUpdate
        // (response body no longer contains the removed line).
        if (requestedQuantity === 0 && preMutationItem) {
          fireDataLayerEvent('remove_from_cart', {
            currency: 'USD',
            value: preMutationItem.price * preMutationItem.quantity,
            items: [preMutationItem]
          });
        }
        return refreshForContext(lineEl).then(function () {
          if (generations.get(lineKey) !== thisGeneration) return;
          restoreCartFocus(focusIntents.get(lineKey));
          focusIntents.delete(lineKey);
        });
      }).catch(function () {
        if (generations.get(lineKey) !== thisGeneration) return;
        setError('Network error. Try again.');
        if (cartDrawer) cartDrawer.open();
      }).then(function () {
        if (generations.get(lineKey) !== thisGeneration) return;
        if (lineEl) lineEl.removeAttribute('aria-busy');
      });
    }

    // Debounce helper for direct qty input edits.
    var debounceTimers = new Map();  // line key → timer id
    function debouncedUpdate (lineKey, quantity, lineEl, delay) {
      var existing = debounceTimers.get(lineKey);
      if (existing) clearTimeout(existing);
      var timer = setTimeout(function () {
        debounceTimers.delete(lineKey);
        cartLineUpdate(lineKey, quantity, lineEl);
      }, delay);
      debounceTimers.set(lineKey, timer);
    }

    // 3.5-4 refactor: document-delegated listeners (was drawer-bound at 3.5-3).
    // Selectors are cart-prefixed (data-cart-qty-* vs PDP's data-qty-*) so
    // delegation namespace is clean — drawer + /cart page surfaces caught;
    // PDP qty stepper unaffected. Future stepper contexts (Phase 4+ quick-add)
    // should use distinct prefixed hooks to preserve the namespace boundary.
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-cart-qty-decrement], [data-cart-qty-increment]');
      if (!btn) return;
      var qtyContainer = btn.closest('[data-cart-qty]');
      var input = qtyContainer && qtyContainer.querySelector('[data-cart-qty-input]');
      if (!input) return;
      var current = parseInt(input.value, 10);
      if (!Number.isFinite(current)) current = 1;
      var min = parseInt(input.min, 10);
      if (!Number.isFinite(min)) min = 1;
      var next = btn.matches('[data-cart-qty-decrement]')
        ? Math.max(min, current - 1)
        : current + 1;
      if (next === current) return;
      input.value = next;
      recordFocusIntent(input.dataset.lineKey, cartFocusKind(btn), input.closest('.hh-cart-line'));
      // Brief button disable for rapid-click defense; cleared by aria-busy clear in cartLineUpdate
      btn.disabled = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      setTimeout(function () { btn.disabled = false; }, 150);
    });

    // Change listener on qty input — debounced AJAX trigger.
    document.addEventListener('change', function (e) {
      var input = e.target.closest('[data-cart-qty-input]');
      if (!input) return;
      var lineKey = input.dataset.lineKey;
      if (!lineKey) return;
      var quantity = parseInt(input.value, 10);
      if (!Number.isFinite(quantity) || quantity < 1) {
        input.value = 1;
        quantity = 1;
      }
      var lineEl = input.closest('.hh-cart-line');
      // Direct edit: keep the +/- intent if that is what changed the value.
      if (!focusIntents.has(lineKey) && document.activeElement === input) recordFocusIntent(lineKey, 'input', lineEl);
      debouncedUpdate(lineKey, quantity, lineEl, 250);
    });

    // Delegated click on remove button — immediate remove (Decision 5-A).
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-cart-line-remove]');
      if (!btn) return;
      var lineKey = btn.dataset.lineKey;
      if (!lineKey) return;
      var lineEl = btn.closest('.hh-cart-line');
      recordFocusIntent(lineKey, 'remove', lineEl);
      cartLineUpdate(lineKey, 0, lineEl);
    });

    // /cart page form intentionally has no submit listener for QTY MUTATIONS.
    // JS-on flow handles qty mutations via individual change events (debounced);
    // native form-POST is the JS-off path only. .no-js CSS gate keeps Update
    // button hidden for JS-on buyers, so they never reach native qty submit.
    //
    // begin_checkout submit listener (3.5-5) IS document-delegated below — it
    // catches the Checkout button submit on both drawer + /cart page forms,
    // fires GA4 event BEFORE form proceeds to /checkout. Different from qty
    // mutations: checkout is a form-POST flow we want to keep (Shopify's
    // canonical /checkout entry), so we observe rather than intercept.
  }

  // ----- /cart pageview view_cart event (3.5-5) -----
  // Fires once per pageview when buyer lands on /cart route. Sets the
  // viewCartFiredThisPageview flag so subsequent drawer-opens skip duplicate
  // (Decision 1-A: single-fire per pageview).
  function initCartView () {
    if (!document.body.classList.contains('template-cart')) return;
    if (viewCartFiredThisPageview) return;
    viewCartFiredThisPageview = true;
    var pageEl = document.querySelector('[data-hh-cart-page]');
    var items = buildFullCartItems(pageEl);
    fireDataLayerEvent('view_cart', {
      currency: 'USD',
      value: readCartTotalFromDom(pageEl),
      items: items
    });
  }

  // ----- Collection view_item_list event (9-β / restructured 2026-05-11) -----
  // Reads server-injected ecommerce payload from <script data-hh-view-item-list>
  // emitted by sections/main-collection.liquid inside the paginate.items > 0
  // branch. Fires via fireDataLayerEvent helper for consistency with the 4
  // Phase 3.5 cart events (view_cart / add_to_cart / remove_from_cart /
  // begin_checkout). Server-render → DOM JSON → JS parse → helper fire.
  //
  // Skip-by-placement: if the section omits the JSON block (empty collection
  // or filter-result-zero state), querySelector returns null + we return
  // early. No template-class check needed.
  //
  // GA4 forwarding: ⚠ THE CLAIM BELOW EXPIRED. Read this before rebuilding
  // the GTM tag on its strength.
  //
  // TRUE ON 2026-05-11, empirically verified via a GA4 Realtime sweep on a
  // collection page: the Shopify Channel App auto-fired 7 standard ecommerce
  // events to GA4 but NOT view_item_list. That is why GTM-5QBZJ7RR carried a
  // Custom Event trigger + GA4 Event tag forwarding it.
  //
  // NO LONGER TRUE. Shopify added view_item_list coverage some time after; the
  // live web-pixels config now lists {"type":"view_item_list",
  // "action_label":"G-XZG4NQD63Y"} — the same GA4 property. Nothing announced
  // the change, so both surfaces fired it and GA4 read 1.49x list-capable page
  // views on 2026-08-26 (view_item, single-sourced, read a clean 1.07x).
  //
  // GTM version 7 (2026-08-26) PAUSED the GA4 - view_item_list tag. Shopify now
  // owns the event. THIS dataLayer push stays — it is the theme's own event and
  // costs nothing; only the GTM forwarding tag was paused, so the push is
  // available if the platform ever drops coverage again.
  //
  // The lesson worth keeping: a capability check against a third-party platform
  // is true as of its DATE, not forever. See docs/living/measurement-architecture.md.
  function initCollectionViewItemList () {
    var dataEl = document.querySelector('script[data-hh-view-item-list]');
    if (!dataEl) return;
    try {
      var ecommerce = JSON.parse(dataEl.textContent);
      fireDataLayerEvent('view_item_list', ecommerce);
    } catch (e) {
      console.warn('[collection-view_item_list] payload parse failed:', e);
    }
  }

  // ----- begin_checkout document-delegated submit listener (3.5-5) -----
  // Catches click + keyboard Enter (both fire submit event). Fires BEFORE
  // form proceeds to /checkout. dataLayer.push is synchronous (queues event);
  // GTM beacon transport handles in-flight transmission across navigation.
  // Refinement 11.D: race-prone-timing tested at 3.5-6 via GA4 DebugView.
  function initBeginCheckout () {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.querySelector) return;
      var checkoutBtn = form.querySelector('button[name="checkout"]');
      if (!checkoutBtn) return;
      // Find the cart container — drawer or /cart page; full cart items[] from there
      var rootEl = form.closest('#hh-cart-drawer, [data-hh-cart-page]');
      var items = buildFullCartItems(rootEl);
      fireDataLayerEvent('begin_checkout', {
        currency: 'USD',
        value: readCartTotalFromDom(rootEl),
        items: items
      });
      // Don't preventDefault — form proceeds to /checkout normally.
    });
  }

  // Reset view_cart fire gate on bfcache restoration (refinement 11.C).
  // Browsers restoring from bfcache preserve closure-local state, which
  // would mean view_cart never re-fires for buyers who use back-forward.
  window.addEventListener('pageshow', function (e) {
    if (e.persisted) viewCartFiredThisPageview = false;
  });

  // ----- Consent signal (Phase 5 follow-up; companion to Privacy v1.4) -----
  // Reads hh_ads_optout cookie + navigator.globalPrivacyControl on every page;
  // pushes consent_state to dataLayer so GTM ad tags can gate firing on consent.
  // GTM ad tag configuration ships with the pixel-ship successor worktree;
  // this signal is preparatory infrastructure that ships now alongside the
  // /pages/data-sharing-opt-out widget.
  var HH_OPTOUT_COOKIE = 'hh_ads_optout';

  function hhReadOptOutCookie () {
    var match = document.cookie.split('; ').find(function (row) {
      return row.indexOf(HH_OPTOUT_COOKIE + '=') === 0;
    });
    return match ? match.split('=')[1] : null;
  }

  function hhDetectGpc () {
    return navigator.globalPrivacyControl === true;
  }

  function hhPushConsentState () {
    var optedOut = hhReadOptOutCookie() === '1';
    var gpc = hhDetectGpc();
    var denied = optedOut || gpc;
    window.dataLayer = window.dataLayer || [];
    // Custom event for non-Google tags (Meta Pixel etc.) consumed via GTM
    // Custom Event trigger on `advertising_consent`.
    window.dataLayer.push({
      event: 'consent_state',
      advertising_consent: denied ? 'denied' : 'granted'
    });
    // Consent Mode v2 update for Google tags (Google Ads, GA4).
    // Default declaration in snippets/analytics.liquid sets initial state
    // from cookie+GPC at page load; this propagates within-session
    // preference changes (user clicks opt-out / opt-in on
    // /pages/your-privacy-choices). gtag is defined globally in
    // analytics.liquid; using arguments-object shape via gtag() is the
    // GTM-canonical form (dataLayer.push of a literal array is not
    // recognized by GTM as a Consent Mode update).
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        'ad_storage': denied ? 'denied' : 'granted',
        'ad_user_data': denied ? 'denied' : 'granted',
        'ad_personalization': denied ? 'denied' : 'granted'
      });
    }
    // Shopify Customer Privacy API — propagates consent to Shopify-managed
    // pixels installed via Sales Channel Apps (Facebook & Instagram Channel,
    // Microsoft Channel, etc.). Without this, the Meta Pixel installed via
    // Shopify Channel App would continue firing for opted-out visitors
    // because Shopify reads its own Customer Privacy state, not our
    // hh_ads_optout cookie.
    //
    // Categories:
    //   marketing      — advertising cookies (Meta Pixel, etc.)
    //   analytics      — analytics cookies (GA4, Dynatrace) — stays granted
    //                    per Privacy Policy v1.4 §5 Analytics cookies
    //   preferences    — site preferences (essential UX)
    //   sale_of_data   — CCPA "sale of data" opt-out signal
    //
    // customerPrivacy is LAZY: measured 2026-10-13 on live, it is undefined
    // at DOMContentLoaded, so the old "skip when absent" branch skipped on
    // every page and opted-out visitors kept marketingAllowed() === true.
    // Shopify.loadFeatures fetches it on demand.
    //
    // Write only when Shopify's state differs from ours. A visitor who never
    // opted out is left on the store's regional default, rather than having
    // "granted" written into a consent cookie on their behalf.
    hhWithCustomerPrivacy(function (cp) {
      if (typeof cp.marketingAllowed === 'function' && cp.marketingAllowed() === !denied) return;
      try {
        cp.setTrackingConsent({
          marketing: !denied,
          analytics: true,
          preferences: true,
          sale_of_data: !denied
        }, function () { /* callback no-op; Shopify confirms write */ });
      } catch (e) {
        console.warn('[consent] Shopify.customerPrivacy.setTrackingConsent failed:', e);
      }
    });
  }

  function hhWithCustomerPrivacy (fn) {
    var S = window.Shopify;
    if (S && S.customerPrivacy && typeof S.customerPrivacy.setTrackingConsent === 'function') return fn(S.customerPrivacy);
    if (!S || typeof S.loadFeatures !== 'function') return;
    try {
      S.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) {
        if (err || !window.Shopify.customerPrivacy) { console.warn('[consent] consent-tracking-api did not load:', err); return; }
        fn(window.Shopify.customerPrivacy);
      });
    } catch (e) {
      console.warn('[consent] Shopify.loadFeatures failed:', e);
    }
  }

  function initConsentSignal () {
    hhPushConsentState();
  }

  // ----- Your Privacy Choices page (Phase 5 follow-up) -----
  // Three-state UI on /pages/data-sharing-opt-out: default (opted-in) /
  // opted-out / gpc. Cookie hh_ads_optout=1 persists opt-out preference
  // for 1 year, host-only, SameSite=Lax. Button clicks toggle the cookie
  // and refresh the UI + re-push consent_state to dataLayer.
  function initChoicesPage () {
    var page = document.querySelector('[data-choices-page]');
    if (!page) return;

    var COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

    function setOptOut () {
      document.cookie = HH_OPTOUT_COOKIE + '=1; Path=/; Max-Age=' + COOKIE_MAX_AGE + '; SameSite=Lax';
    }

    function clearOptOut () {
      document.cookie = HH_OPTOUT_COOKIE + '=; Path=/; Max-Age=0; SameSite=Lax';
    }

    function currentState () {
      if (hhDetectGpc()) return 'gpc';
      if (hhReadOptOutCookie() === '1') return 'opted-out';
      return 'default';
    }

    function showState (stateName) {
      page.querySelectorAll('[data-choices-state]').forEach(function (panel) {
        var matches = panel.getAttribute('data-choices-state') === stateName;
        panel.hidden = !matches;
        panel.setAttribute('aria-hidden', String(!matches));
      });
    }

    function refresh () {
      showState(currentState());
      hhPushConsentState();
    }

    page.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-choices-action]');
      if (!trigger) return;
      var action = trigger.getAttribute('data-choices-action');
      if (action === 'opt-out') setOptOut();
      if (action === 'opt-in') clearOptOut();
      refresh();
    });

    refresh();
  }

  // ---------------------------------------------------------------
  // initPredictiveSearch (Collections Phase 2, 2026-07-05)
  //
  // Type-ahead for the header + mobile-drawer search bars. Storefront
  // audit finding: Shopify's predictive endpoint is live but the theme
  // shipped no UI. Attaches to any [data-hh-predictive] .sb container:
  //   - Debounced fetch to routes.predictive_search_url (/search/suggest.json)
  //     for products + collections; AbortController cancels stale requests.
  //   - Renders into the atom-spec'd .sb-suggest panel (CSS already present):
  //     OEM-PN-detect row (.sb-suggest-pn) · Parts group (.sb-sg-parts, with
  //     thumbnail + title + price) · Categories chips (.sb-sg-cats) ·
  //     "See all results" footer (.sb-suggest-all).
  //   - ARIA combobox pattern: input role=combobox + aria-expanded +
  //     aria-controls + aria-activedescendant; panel role=listbox; each
  //     actionable row role=option. Keyboard: ↓/↑ move, Enter opens the
  //     active row (else submits the form), Esc closes, click-outside closes.
  //
  // Progressive enhancement: with JS off (or a failed fetch), the form still
  // submits on Enter to routes.search_url — standard Shopify search. This
  // function only ADDS a panel; it never blocks the native form.
  // ---------------------------------------------------------------
  function initPredictiveSearch () {
    var bars = document.querySelectorAll('[data-hh-predictive]');
    if (!bars.length) return;

    var MIN = 2;          // min query length before fetching
    var DEBOUNCE = 180;   // ms
    var LIMIT = 6;        // per resource type

    function esc (s) {
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }
    function money (v) {
      var n = parseFloat(v);
      if (isNaN(n)) return '';
      return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    // OEM-PN heuristic: no spaces, ≥7 chars, at least one digit, alnum/dash
    // only. Catches "84136206", "P52855644AD", "1K0614517EK". Excludes
    // multi-word vehicle queries (a space disqualifies).
    function looksLikePn (q) {
      var raw = q.trim();
      return !/\s/.test(raw) && raw.length >= 7 && /\d/.test(raw) && /^[A-Za-z0-9-]+$/.test(raw);
    }

    var seq = 0;
    bars.forEach(function (sb) {
      var input = sb.querySelector('.sb-input');
      var form = sb.querySelector('form');
      if (!input || !form) return;

      var predictiveBase = (sb.getAttribute('data-predictive-url') || '/search/suggest');
      var searchUrl = (sb.getAttribute('data-search-url') || '/search');
      var panelId = 'sb-suggest-' + (seq++);

      var panel = document.createElement('div');
      panel.className = 'sb-suggest';
      panel.id = panelId;
      panel.setAttribute('role', 'listbox');
      panel.hidden = true;
      sb.appendChild(panel);

      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-expanded', 'false');
      input.setAttribute('aria-controls', panelId);
      input.setAttribute('aria-haspopup', 'listbox');

      var options = [];       // [HTMLElement] actionable rows
      var activeIndex = -1;
      var timer = null;
      var ctl = null;         // AbortController
      var reqId = 0;
      var lastRendered = '';  // query the panel currently reflects

      function fullSearchHref (q) {
        return searchUrl + '?q=' + encodeURIComponent(q.trim());
      }
      function close () {
        if (panel.hidden) return;
        panel.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
        activeIndex = -1;
      }
      function openIfContent () {
        if (options.length && panel.hidden) {
          panel.hidden = false;
          input.setAttribute('aria-expanded', 'true');
        }
      }
      function setActive (i) {
        options.forEach(function (el) { el.classList.remove('is-active'); el.setAttribute('aria-selected', 'false'); });
        activeIndex = i;
        if (i >= 0 && options[i]) {
          options[i].classList.add('is-active');
          options[i].setAttribute('aria-selected', 'true');
          input.setAttribute('aria-activedescendant', options[i].id);
          options[i].scrollIntoView({ block: 'nearest' });
        } else {
          input.removeAttribute('aria-activedescendant');
        }
      }

      function render (data, q) {
        var results = (data && data.resources && data.resources.results) || {};
        var products = results.products || [];
        var collections = results.collections || [];
        var html = '';
        var oid = 0;
        function nextId () { return panelId + '-opt-' + (oid++); }

        if (looksLikePn(q)) {
          html += '<a id="' + nextId() + '" role="option" aria-selected="false" class="sb-suggest-pn" href="' + esc(fullSearchHref(q)) + '">'
            + '<span class="sb-pn-icon" aria-hidden="true">#</span>'
            + '<span class="sb-pn-lbl">Search part number</span>'
            + '<code class="sb-pn-val hh-pn__value">' + esc(q.trim()) + '</code>'
            + '<span class="sb-pn-arrow" aria-hidden="true">&rarr;</span></a>';
        }

        if (products.length) {
          html += '<div class="sb-sg sb-sg-parts"><p class="sb-sg-h hh-eyebrow">Parts</p><ul class="sb-sg-list">';
          products.slice(0, LIMIT).forEach(function (p) {
            var img = p.image || (p.featured_image && p.featured_image.url) || '';
            // The ProductCard row variant's markup and classes (snippets/card-product.liquid,
            // variant: 'row'), built by hand because this panel is JS-rendered (GH #3436).
            var thumb = img
              ? '<span class="hh-card-product__thumb" style="background-image:url(' + esc(img) + ')" aria-hidden="true"></span>'
              : '<span class="hh-card-product__thumb" aria-hidden="true"></span>';
            var price = money(p.price);
            html += '<li><a id="' + nextId() + '" role="option" aria-selected="false" class="hh-card-product hh-card-product--row hh-card-product--flush" href="' + esc(p.url) + '">'
              + thumb
              + '<span class="hh-card-product__body">'
              + (p.type ? '<span class="hh-card-product__eyebrow hh-eyebrow">' + esc(p.type) + '</span>' : '')
              + '<span class="hh-card-product__title">' + esc(p.title) + '</span>'
              + (price ? '<span class="hh-card-product__meta"><span class="hh-card-product__price">' + price + '</span></span>' : '')
              + '</span></a></li>';
          });
          html += '</ul></div>';
        }

        if (collections.length) {
          html += '<div class="sb-sg"><p class="sb-sg-h hh-eyebrow">Categories</p><ul class="sb-sg-cats">';
          collections.slice(0, LIMIT).forEach(function (c) {
            html += '<li><a id="' + nextId() + '" role="option" aria-selected="false" href="' + esc(c.url) + '">' + esc(c.title) + '</a></li>';
          });
          html += '</ul></div>';
        }

        if (!products.length && !collections.length) {
          html += '<div class="sb-sg"><p class="sb-sg-h hh-eyebrow">No quick matches — try the full search</p></div>';
        }

        html += '<a id="' + nextId() + '" role="option" aria-selected="false" class="sb-suggest-all" href="' + esc(fullSearchHref(q)) + '">See all results for &ldquo;' + esc(q.trim()) + '&rdquo; &rarr;</a>';

        panel.innerHTML = html;
        options = Array.prototype.slice.call(panel.querySelectorAll('[role="option"]'));
        activeIndex = -1;
        lastRendered = q.trim();
        openIfContent();
      }

      function fetchSuggest (q) {
        if (ctl) ctl.abort();
        ctl = ('AbortController' in window) ? new AbortController() : null;
        var mine = ++reqId;
        var url = predictiveBase + '.json'
          + '?q=' + encodeURIComponent(q)
          + '&resources[type]=product,collection'
          + '&resources[limit]=' + LIMIT
          + '&resources[options][unavailable_products]=last';
        fetch(url, { headers: { 'Accept': 'application/json' }, signal: ctl ? ctl.signal : undefined })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (data) {
            if (mine !== reqId || !data) return;          // stale or failed
            if (input.value.trim().length < MIN) { close(); return; }
            render(data, q);
          })
          .catch(function () { /* aborted or network error — leave form intact */ });
      }

      // ── Events ──
      input.addEventListener('input', function () {
        var q = input.value.trim();
        if (timer) clearTimeout(timer);
        if (q.length < MIN) { close(); return; }
        if (q === lastRendered && options.length) { openIfContent(); return; }
        timer = setTimeout(function () { fetchSuggest(q); }, DEBOUNCE);
      });

      input.addEventListener('focus', function () {
        if (input.value.trim().length >= MIN && options.length) openIfContent();
      });

      input.addEventListener('keydown', function (e) {
        if (e.key === 'ArrowDown') {
          if (panel.hidden && options.length) { openIfContent(); setActive(0); e.preventDefault(); return; }
          if (options.length) { setActive((activeIndex + 1) % options.length); e.preventDefault(); }
        } else if (e.key === 'ArrowUp') {
          if (options.length) { setActive((activeIndex - 1 + options.length) % options.length); e.preventDefault(); }
        } else if (e.key === 'Enter') {
          if (!panel.hidden && activeIndex >= 0 && options[activeIndex]) {
            e.preventDefault();
            window.location.href = options[activeIndex].getAttribute('href');
          }
          // else: let the form submit natively (full search)
        } else if (e.key === 'Escape') {
          if (!panel.hidden) { close(); e.stopPropagation(); }
        }
      });

      // Hovering a row makes it the active descendant (pointer + keyboard parity)
      panel.addEventListener('mousemove', function (e) {
        var opt = e.target.closest ? e.target.closest('[role="option"]') : null;
        if (opt) { var i = options.indexOf(opt); if (i >= 0 && i !== activeIndex) setActive(i); }
      });

      // Click-outside closes
      document.addEventListener('click', function (e) {
        if (!sb.contains(e.target)) close();
      });
      // Closing the drawer / blurring away
      input.addEventListener('blur', function () {
        // Delay so a click on a panel row registers before we hide it.
        setTimeout(close, 120);
      });
    });
  }

  ready(function () {
    initAnnouncement();
    initMobileNav();
    initSpecCopy();
    initPdpGallery();
    initQty();
    initVariants();
    initFitmentStatus();
    initYmmDemandSignal();
    initVehicleBanner();
    initPdpStickyAtc();
    initPdpRelated();
    initSoldOutRecoveryEvents();
    initDiagnosticVideo();
    initCollectionSort();
    initCollectionFilters();
    initBrowseControlResync();
    initFilterShowMore();
    initCollectionYmmPicker();
    initCollectionFilterDrawer();
    initLoadMore();
    initCollectionVehicleBanner();
    initCollectionStrandedFilterRecovery();
    initSearchYmmPicker();
    initSearchStrandedFilterRecovery();
    initSearchPnFallback();
    initCartDrawer();
    initCartVehicleBar();
    initYmmCard({ prefix: 'cart-empty' });
    initYmmCard({ prefix: 'hero' });
    // /pages/find-your-vehicle (GH #762). Same shared picker, same submit
    // path (setStoredVehicle -> buildVehicleFilterUrl); no-ops on every other
    // template because the [data-fyv-ymm] form is not in the DOM.
    initYmmCard({ prefix: 'fyv' });
    initAjaxAddToCart();
    initCartMutations();
    initCartView();
    initCollectionViewItemList();
    initBeginCheckout();
    initConsentSignal();
    initChoicesPage();
    initPredictiveSearch();
  });
})();
